import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "./context/api";
import { useAuth } from "./context/AuthContext";


const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return isNaN(date) ? 'Invalid date' : date.toLocaleDateString('en-IN');
};

function Scanners()  {
  const TABS = [
    "Monthly Cycles",
    "Price + OI + PCR",
    "Price + Delivery + ATS",
    "Price + 52W H-L + SMA",
    "IPO Tracking",
    "Share Holding Change",
    "Golden Cross",
    "Death Cross",
    "Deep Recovery",
  ];

  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [showExplanation, setShowExplanation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [monthlyCycleData, setMonthlyCycleData] = useState({}); // API returns object: { "January-2026": [...] }

  const [technicalsData, setTechnicalsData] = useState({});
  const [ipoTrackingData, setIpoTrackingData] = useState({});
  const [showMissingNamesOnly, setShowMissingNamesOnly] = useState(false);

  const { user, initializing } = useAuth();

  const [sortConfig, setSortConfig] = React.useState({
    key: "chg",
    direction: "desc",
  });

  const [crossSort, setCrossSort] = useState({
    key: "crossDateTs",
    direction: "desc",
  });

  // What is ACTUALLY applied to the table
  const [appliedPrice52Filters, setAppliedPrice52Filters] = useState({
    chg: { op: ">", value: "" },
    marketCapCr: { op: ">", value: "" },
    fiftyTwoWeekHigh_diff_per: { op: ">", value: "" },
    fiftyDayAverage_diff_per: { op: ">", value: "" },
    twoHundredDayAverage_diff_per: { op: ">", value: "" },
  });

  const price52FilterRefs = useRef({});

  // ✅ per-month table sorting (independent)
  const [monthlySort, setMonthlySort] = useState({}); 
  // shape: { [monthKey]: { key: "avg1m"|"totalChg"|"chg1d", direction: "asc"|"desc" } }

  const getMonthlySort = (monthKey) =>
    monthlySort[monthKey] || { key: "avg1m", direction: "desc" };


  useEffect(() => {
    api
      .get("/api/scanners/monthly_cycles")
      .then((res) => setMonthlyCycleData(res.data))
      .catch((err) => console.error("Failed to fetch monthly cycles", err));
  }, []);

  useEffect(() => {
    setLoading(true);

    api
      .get("/api/scanners/technicals")
      .then((res) => setTechnicalsData(res.data))
      .catch((err) => console.error("Failed to fetch Technical data", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setLoading(true);

    api
      .get("/api/ipoTracking")
      .then((res) => setIpoTrackingData(res.data))
      .catch((err) => console.error("Failed to fetch IPO tracking data", err))
      .finally(() => setLoading(false));
  }, []);

  const tips = {
    "Monthly Cycles": (
      <>
        <p className="mb-2">
          This scanner helps you identify <b>repeatable monthly behaviour</b> in stocks or sectors.Certain stocks tend to perform well in specific months due to:
        </p>
        <ul className="list-disc ml-5 mb-2">
          <li>Quarterly result cycles</li>
          <li>Budget, policy, or macro seasonality</li>
          <li>Institutional portfolio rebalancing</li>
        </ul>
        <p>
          Use this to align your swing trades with <b>historical probability</b>, not prediction.
        </p>
      </>
    ),

    "Price + OI + PCR": (
      <>
        <p className="mb-2">
          This scanner combines <b>price action</b> with <b>derivative participation</b>.
        </p>
        <ul className="list-disc ml-5 mb-2">
          <li><b>Price</b> tells you direction</li>
          <li><b>Open Interest</b> = Total Open Futures contracts in percentage terms. This tells you participation. Use this along with Price Change. Also looks for divergence i.e price going up but OI going down.</li>
          <li><b>PCR</b> = Total PE Open contracts/Total CE Open Contracts. This ratio hints where positioning is skewed towards Call writers or Put writers. If There are more call writers it can act as resistance, if there are more put writers it can act as
          support.</li>
        </ul>
        <p className="mb-2">
          The goal is <b>not to do options trading</b>, but understanding market structure:
        </p>
        <ul className="list-disc ml-5">
          <li>Fresh buildup vs short covering</li>
          <li>Supported by participation or getting exhausted</li>
        </ul>
      </>
    ),

    "Price + Delivery + ATS": (
      <>
        <p className="mb-2">
          This scanner focuses on <b>cash-market conviction</b>.
        </p>
        <ul className="list-disc ml-5 mb-2">
          <li><b>Price</b> tells you direction</li>
          <li><b>Delivery %</b> = Total Delivery Volume in percentage terms.This shows how much volume turned into ownership, this removes bulk deals.</li>
          <li><b>ATS</b> (Average Trade Size) = Total Volume/Total Number of trades.This indicates institutional vs retail activity.
          If ATS is high it can be insider or institutional activity. If ATS is low it can be retail activity.
          </li>
        </ul>
        <p className="mb-2">
          A price move with <b>high delivery and rising ATS</b> at lower level usually signals:
        </p>
        <ul className="list-disc ml-5">
          <li>Stronger hands entering</li>
        </ul>
      </>
    ),

    "Correlation + Arbitrage": (
      <>
        <p className="mb-2">
          This scanner looks for <b>relative mispricing</b> instead of absolute direction.
        </p>
        <ul className="list-disc ml-5 mb-2">
          <li>Stock vs Sector divergence</li>
          <li>Peer-to-peer relative weakness or strength</li>
          <li>Equity vs commodity or macro link breaks</li>
          <li>Indian Stocks vs Global Peers</li>
          <li>Revaluations due to new IPO</li>
        </ul>
        <p>
          Useful when markets are range-bound and <b>alpha comes from relationships</b>,
          not trends.
        </p>
      </>
    ),
  };

  const monthKeys = useMemo(() => {
    // Try to sort by YYYY-MM then fallback to alpha
    const keys = Object.keys(monthlyCycleData || {});
    const monthMap = {
      january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
      july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
    };

    const parseKey = (k) => {
      // "January-2026"
      const [mNameRaw, yRaw] = String(k).split("-");
      const y = Number(yRaw);
      const m = monthMap[String(mNameRaw || "").trim().toLowerCase()] || 0;
      return { y: isFinite(y) ? y : 0, m };
    };

    return keys.sort((a, b) => {
      const A = parseKey(a);
      const B = parseKey(b);
      if (A.y !== B.y) return A.y - B.y;   // oldest year first
      if (A.m !== B.m) return A.m - B.m;   // oldest month first
      return String(a).localeCompare(String(b));
    });
  }, [monthlyCycleData]);

  const technicalsMap = useMemo(() => {
    const map = {};
    const arr = Array.isArray(technicalsData)
      ? technicalsData
      : Object.values(technicalsData || {});

    arr.forEach(t => {
      if (t?.symbol) {
        map[t.symbol] = t;
      }
    });

    return map;
  }, [technicalsData]);

  const ColorChange = ({ value, suffix = "%", digits = 2 }) => {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return <span className="text-gray-500">—</span>;

    const color =
      n > 0 ? "text-green-600" : n < 0 ? "text-red-600" : "text-gray-600";

    return (
      <span className={color}>
        {`${n > 0 ? "+" : ""}${n.toFixed(digits)}${suffix}`}
      </span>
    );
  };

  const SortableTh = ({ label, sortKey, subLabel, align = "right" }) => {
    const active = sortConfig.key === sortKey;
    const dir = sortConfig.direction;

    return (
      <th
        onClick={() =>
          setSortConfig((prev) => ({
            key: sortKey,
            direction:
              prev.key === sortKey && prev.direction === "desc" ? "asc" : "desc",
          }))
        }
          className={`cursor-pointer select-none px-3 py-2 ${align === "left" ? "text-left" : "text-right"} ${
          active ? "text-gray-900" : ""
        }`}
      >
        <div className={`flex flex-col ${align === "left" ? "items-start" : "items-end"}`}>
          <span className="flex items-center gap-1 text-blue-600">
            {label}
            {active && (dir === "asc" ? "↑" : "↓")}
          </span>
          {subLabel && <span className="text-xs text-gray-400">{subLabel}</span>}
        </div>
      </th>
    );
  };

  const MonthlySortableTh = ({ monthKey, label, sortKey }) => {
    const cfg = getMonthlySort(monthKey);
    const active = cfg.key === sortKey;
    const dir = cfg.direction;

    return (
      <th
        onClick={() =>
          setMonthlySort((prev) => {
            const cur = prev[monthKey] || { key: "avg1m", direction: "desc" };
            const nextDir =
              cur.key === sortKey && cur.direction === "desc" ? "asc" : "desc";
            return { ...prev, [monthKey]: { key: sortKey, direction: nextDir } };
          })
        }
        className="border px-2 py-1 text-left whitespace-nowrap sticky top-0 bg-gray-100 z-20 cursor-pointer select-none"
        title="Sort"
      >
        <span className="inline-flex items-center gap-1 text-blue-600">
          {label}
          {active && (dir === "asc" ? "↑" : "↓")}
        </span>
      </th>
    );
  };

  const toNum = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  const fmt = (v, d = 2) => {
    const n = toNum(v);
    return n === null ? "—" : n.toFixed(d);
  };

  const pct = (curr, prev) => {
    const c = toNum(curr);
    const p = toNum(prev);
    if (c === null || p === null || p === 0) return null;
    return ((c - p) / p) * 100;
  };

  const renderMonthlyCycles = () => {
    if (loading) {
      return <div className="mt-3 text-sm text-gray-500">Loading…</div>;
    }

    if (!monthKeys.length) {
      return <div className="mt-3 text-sm text-red-500">No data available at the moment</div>;
    }

    const escapeCsv = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      // wrap if it contains comma, quote, or newline
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const downloadCsv = (rows, filename = "export.csv") => {
      if (!rows || rows.length === 0) return;

      const cols = Object.keys(rows[0]).filter((c) => !String(c).startsWith("__"));

      const header = cols.map(escapeCsv).join(",");
      const body = rows
        .map((r) => cols.map((c) => escapeCsv(r?.[c])).join(","))
        .join("\n");

      const csv = header + "\n" + body;

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    };


    return (
      <div className="mt-4 space-y-2">
        <div className="mt-2 text-gray-500">
          List Updates every month on 1st. Displays Live Updates.
        </div>

        {monthKeys.map((monthKey) => {
          const rowsRaw = monthlyCycleData?.[monthKey] || [];
          const cfg = getMonthlySort(monthKey);

          const rowsEnriched = (rowsRaw || []).map((r) => {
            const recordPrice = Number(r?.recordPrice);
            const currentPrice = Number(r?.currentPrice);
            const prevClose = Number(r?.previousClose);

            const totalChg =
              Number.isFinite(currentPrice) &&
              Number.isFinite(recordPrice) &&
              recordPrice !== 0
                ? ((currentPrice - recordPrice) / recordPrice) * 100
                : null;

            const chg1d =
              Number.isFinite(currentPrice) &&
              Number.isFinite(prevClose) &&
              prevClose !== 0
                ? ((currentPrice - prevClose) / prevClose) * 100
                : null;

            const avg1mRaw = r?.Avg_1M ?? r?.avg_1m;
            const avg1m = Number.isFinite(Number(avg1mRaw))
              ? Number(avg1mRaw)
              : null;

            const marketCapCr = Number.isFinite(Number(r?.MarketCapCr))
              ? Number(r?.MarketCapCr)
              : null;

            return {
              ...r,
              __totalChg: totalChg,
              __chg1d: chg1d,
              __avg1m: avg1m,
              __marketCapCr: marketCapCr,
            };
          });

          const rows = [...rowsEnriched].sort((a, b) => {
            const dir = cfg.direction === "asc" ? 1 : -1;

            const getVal = (x) => {
              switch (cfg.key) {
                case "totalChg":
                  return x.__totalChg;
                case "chg1d":
                  return x.__chg1d;
                case "marketCapCr":
                  return x.__marketCapCr;
                case "avg1m":
                default:
                  return x.__avg1m;
              }
            };

            const av = getVal(a);
            const bv = getVal(b);

            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;

            return (av - bv) * dir;
          });

          const cols = rows.length
            ? Object.keys(rows[0]).filter((c) => !String(c).startsWith("__"))
            : [];

          const hasMarketCapCr = cols.includes("MarketCapCr");

          const publishedDate =
            rows.length && rows[0]?.recordDate ? rows[0].recordDate : null;

          const hiddenCols = [
            "Symbol",
            "Name",
            "recordDate",
            "Industry",
            "MarketCapCr",
            "recordPrice",
            "currentPrice",
            "previousClose",
            "lastUpdateDate",
          ];

          return (
            <div key={monthKey} className="border rounded bg-white">
              <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
                <div className="flex items-baseline gap-2">
                  <div className="font-semibold text-gray-800">{monthKey}</div>

                  {publishedDate && (
                    <div className="text-xs text-gray-500">
                      Published on: {formatDate(publishedDate)}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{rows.length} rows</span>

                  <button
                    type="button"
                    onClick={() => {
                      const safeKey = String(monthKey).replace(/[^\w-]+/g, "_");
                      downloadCsv(rows, `monthly_cycles_${safeKey}.csv`);
                    }}
                    className="flex-none px-3 py-1 text-xs border rounded bg-emerald-600 text-white hover:bg-emerald-700"
                    title={`Export ${monthKey} to CSV`}
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              {rows.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">No rows</div>
              ) : (
                <div className="overflow-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr>
                        <th className="border px-2 py-1 text-left whitespace-normal sticky top-0 left-0 bg-gray-100 z-30">
                          Name
                        </th>

                        <th className="border px-2 py-1 text-left whitespace-normal break-words sticky top-0 bg-gray-100 z-20">
                          Industry
                        </th>

                        {hasMarketCapCr && (
                          <MonthlySortableTh
                            monthKey={monthKey}
                            label="Market Cap Cr"
                            sortKey="marketCapCr"
                          />
                        )}

                        <th className="border px-2 py-1 text-left whitespace-nowrap sticky top-0 bg-gray-100 z-20">
                          Published Price
                        </th>

                        <th className="border px-2 py-1 text-left whitespace-nowrap sticky top-0 bg-gray-100 z-20">
                          Current Price
                        </th>

                        <MonthlySortableTh
                          monthKey={monthKey}
                          label="Total Chg"
                          sortKey="totalChg"
                        />

                        <MonthlySortableTh
                          monthKey={monthKey}
                          label="1D Chg"
                          sortKey="chg1d"
                        />

                        {cols
                          .filter((c) => !hiddenCols.includes(c))
                          .map((c) =>
                            String(c).toLowerCase() === "avg_1m" ? (
                              <MonthlySortableTh
                                key={c}
                                monthKey={monthKey}
                                label={c}
                                sortKey="avg1m"
                              />
                            ) : (
                              <th
                                key={c}
                                className="border px-2 py-1 text-left whitespace-nowrap sticky top-0 bg-gray-100 z-20"
                              >
                                {c}
                              </th>
                            )
                          )}
                      </tr>
                    </thead>

                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={idx} className="group hover:bg-blue-50">
                          <td className="p-2 sticky left-0 bg-white z-10 font-medium whitespace-normal group-hover:bg-blue-50">
                            <a
                              href={`/symbol/${r.Symbol}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {r.Name || "—"}
                            </a>
                          </td>

                          <td className="border px-2 py-1 whitespace-normal">
                            {r.Industry || "—"}
                          </td>

                          {hasMarketCapCr && (
                            <td className="border px-2 py-1 whitespace-nowrap">
                              {r.MarketCapCr != null &&
                              String(r.MarketCapCr).trim() !== ""
                                ? Number(r.MarketCapCr).toLocaleString("en-IN", {
                                    maximumFractionDigits: 0,
                                  })
                                : "—"}
                            </td>
                          )}

                          <td className="border px-2 py-1 whitespace-nowrap">
                            {r.recordPrice != null &&
                            String(r.recordPrice).trim() !== ""
                              ? Number(r.recordPrice).toLocaleString("en-IN", {
                                  maximumFractionDigits: 2,
                                })
                              : "—"}
                          </td>

                          <td
                            className={`border px-2 py-1 whitespace-nowrap ${
                              r.currentPrice != null && r.recordPrice != null
                                ? Number(r.currentPrice) > Number(r.recordPrice)
                                  ? "text-green-600"
                                  : "text-red-600"
                                : "text-gray-600"
                            }`}
                          >
                            {r.currentPrice != null
                              ? Number(r.currentPrice).toLocaleString("en-IN", {
                                  maximumFractionDigits: 2,
                                })
                              : "—"}
                          </td>

                          <td
                            className={`border px-2 py-1 whitespace-nowrap ${
                              r.__totalChg != null
                                ? r.__totalChg > 0
                                  ? "text-green-600"
                                  : "text-red-600"
                                : "text-gray-600"
                            }`}
                          >
                            {r.__totalChg != null
                              ? `${r.__totalChg > 0 ? "+" : ""}${r.__totalChg.toFixed(
                                  2
                                )}%`
                              : "—"}
                          </td>

                          <td
                            className={`border px-2 py-1 whitespace-nowrap ${
                              r.__chg1d != null
                                ? r.__chg1d > 0
                                  ? "text-green-600"
                                  : "text-red-600"
                                : "text-gray-600"
                            }`}
                          >
                            {r.__chg1d != null
                              ? `${r.__chg1d > 0 ? "+" : ""}${r.__chg1d.toFixed(
                                  2
                                )}%`
                              : "—"}
                          </td>

                          {cols
                            .filter((c) => !hiddenCols.includes(c))
                            .map((c) => {
                              const raw = r?.[c];

                              let display = "—";
                              if (raw !== null && raw !== undefined && raw !== "") {
                                const num = Number(raw);
                                display = Number.isFinite(num)
                                  ? num.toFixed(1)
                                  : raw;
                              }

                              return (
                                <td
                                  key={c}
                                  className="border px-2 py-1 whitespace-nowrap"
                                >
                                  {display}
                                </td>
                              );
                            })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };


  const renderPriceOiPcr = () => {
    const arr = Array.isArray(technicalsData)
      ? technicalsData
      : Object.values(technicalsData || {});

    if (!arr.length) {
      return (
        <div className="text-sm text-red-500">
          No data available at the moment
        </div>
      );
    }

    // ---------------------------------------------------------
    // SORTING
    // ---------------------------------------------------------

    const sortRows = (rows) => {
      const { key, direction } = sortConfig;
      const dir = direction === "asc" ? 1 : -1;

      return [...rows].sort((a, b) => {
        if (key === "industry") {
          return (
            String(a.industry || "").localeCompare(
              String(b.industry || "")
            ) * dir
          );
        }

        const av = toNum(a?.[key]);
        const bv = toNum(b?.[key]);

        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;

        return (av - bv) * dir;
      });
    };

    // ---------------------------------------------------------
    // EXPIRY HELPERS
    // ---------------------------------------------------------

    const toDateOnly = (value) => {
      if (!value) return null;

      const d = new Date(value);

      if (Number.isNaN(d.getTime())) return null;

      d.setHours(0, 0, 0, 0);

      return d;
    };

    const getLastThursdayOfMonth = (year, monthIndex) => {
      const d = new Date(year, monthIndex + 1, 0);

      while (d.getDay() !== 4) {
        d.setDate(d.getDate() - 1);
      }

      d.setHours(0, 0, 0, 0);

      return d;
    };

    const getMonthlyExpiryAroundDate = (lastUpdateDate) => {
      const base = toDateOnly(lastUpdateDate);

      if (!base) {
        return {
          prevExpiry: null,
          nextExpiry: null,
          daysToNextExpiry: null,
        };
      }

      const y = base.getFullYear();
      const m = base.getMonth();

      const currentMonthExpiry = getLastThursdayOfMonth(y, m);

      let prevExpiry;
      let nextExpiry;

      if (base <= currentMonthExpiry) {
        nextExpiry = currentMonthExpiry;

        const prevMonth = new Date(y, m - 1, 1);

        prevExpiry = getLastThursdayOfMonth(
          prevMonth.getFullYear(),
          prevMonth.getMonth()
        );
      } else {
        prevExpiry = currentMonthExpiry;

        const nextMonth = new Date(y, m + 1, 1);

        nextExpiry = getLastThursdayOfMonth(
          nextMonth.getFullYear(),
          nextMonth.getMonth()
        );
      }

      const daysToNextExpiry = Math.ceil(
        (nextExpiry - base) / (1000 * 60 * 60 * 24)
      );

      return {
        prevExpiry,
        nextExpiry,
        daysToNextExpiry,
      };
    };

    const formatExpiryDate = (d) => {
      if (!d) return "—";

      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      });
    };

    // ---------------------------------------------------------
    // INDUSTRY DISPLAY
    // ---------------------------------------------------------

    const showIndustry = (industry) => {
      if (
        !industry ||
        ["N", "NEW"].includes(
          String(industry).trim().toUpperCase()
        )
      ) {
        return "—";
      }

      return String(industry)
        .split("/")
        .map((x, i) => (
          <div
            key={i}
            className="block w-fit bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full mb-1"
          >
            {x.trim()}
          </div>
        ));
    };

    // ---------------------------------------------------------
    // PREPARE ROWS
    // ---------------------------------------------------------

    const rows = arr
      .filter((x) => {
        const pcr = toNum(x.op_pcr_oi);
        const pcr20 = toNum(x.op_pcr_oi_20_avg);

        return (
          pcr !== null &&
          pcr !== 0 &&
          pcr20 !== null &&
          pcr20 !== 0
        );
      })
      .map((x) => {
        const currentPrice = x.currentPrice;
        const previousClose = x.previousClose;

        return {
          symbol: x.symbol,
          name: x.name,
          industry: x.industry,
          lastUpdateDate: x.lastUpdateDate,

          // Price
          currentPrice,
          previousClose,
          chg: pct(currentPrice, previousClose),

          // Market Cap in Crores
          marketCapCr:
            toNum(x.marketCap) !== null
              ? toNum(x.marketCap) / 10000000
              : null,

          // 50 DMA
          fiftyDayAverage: x.fiftyDayAverage,

          fiftyDayAverage_diff_per: pct(
            currentPrice,
            x.fiftyDayAverage
          ),

          // Futures OI
          oi: x.fut_oi,

          fut_oi_chg_20_med: pct(
            x.fut_oi,
            x.fut_oi_20_avg
          ),

          // PCR
          pcr: x.op_pcr_oi,

          op_pcr_chg_oi_20_avg: pct(
            x.op_pcr_oi,
            x.op_pcr_oi_20_avg
          ),

          // Margin Volume
          margin_vol: x.margin_vol,

          // Margin Volume vs 20 DMA
          margin_volChg20: pct(
            x.margin_vol,
            x.margin_vol_20_dma
          ),

          // Margin Value / Market Cap %
          margin_val_mcap_pct:
            toNum(x.marketCap) > 0 && toNum(x.margin_val) !== null
              ? (toNum(x.margin_val) / toNum(x.marketCap)) * 100
              : null,
        };
      });

    const sortedRows = sortRows(rows);

    if (!sortedRows.length) {
      return (
        <div className="text-sm text-red-500">
          No data available at the moment
        </div>
      );
    }

    const rowCount = sortedRows.length;

    const lastUpdateDate = arr[0]?.lastUpdateDate ?? null;

    const lastExpiryDate = lastUpdateDate
      ? getMonthlyExpiryAroundDate(lastUpdateDate)
      : null;

    return (
      <div>

        {/* ----------------------------------------------------- */}
        {/* STATUS */}
        {/* ----------------------------------------------------- */}

        <div className="mt-2 text-gray-500">
          Updates daily 11 PM. Last updated -{" "}
          {formatDate(lastUpdateDate)}, Last Expiry -{" "}

          <span className="font-semibold text-gray-800">
            {formatExpiryDate(lastExpiryDate?.prevExpiry)}
          </span>
        </div>

        <div className="text-xs sm:text-sm text-gray-400 mb-2">
          Tracked Symbols - {rowCount}
        </div>

        {/* ----------------------------------------------------- */}
        {/* TABLE */}
        {/* ----------------------------------------------------- */}

        <div className="overflow-x-auto">

          <table className="table-fixed border-collapse text-xs sm:text-sm text-gray-800 min-w-[1550px] w-full">

            {/* ------------------------------------------------- */}
            {/* COLUMN WIDTHS */}
            {/* ------------------------------------------------- */}

            <colgroup>
              {/* Name */}
              <col className="w-44 sm:w-72" />

              {/* Price */}
              <col className="w-20" />

              {/* Price Change */}
              <col className="w-20" />

              {/* Market Cap */}
              <col className="w-24" />

              {/* Industry */}
              <col className="w-40" />

              {/* 50 DMA */}
              <col className="w-24" />

              {/* Diff vs 50 DMA */}
              <col className="w-24" />

              {/* Fut OI */}
              <col className="w-20" />

              {/* Fut OI Change */}
              <col className="w-24" />

              {/* PCR */}
              <col className="w-20" />

              {/* PCR Change */}
              <col className="w-24" />

              {/* Margin Volume - NEW */}
              <col className="w-28" />

              {/* Margin Volume Change - NEW */}
              <col className="w-32" />
            </colgroup>

            {/* ------------------------------------------------- */}
            {/* HEADER */}
            {/* ------------------------------------------------- */}

            <thead className="bg-gray-50 text-gray-600">
              <tr>

                {/* Name */}
                <th className="sticky left-0 z-30 bg-gray-50 border-r px-2 sm:px-3 py-2 text-left">
                  Name
                </th>

                {/* Price */}
                <SortableTh
                  label="Price"
                  sortKey="currentPrice"
                />

                {/* Price Change */}
                <SortableTh
                  label="Price"
                  sortKey="chg"
                  subLabel="Chg"
                />

                {/* Market Cap */}
                <SortableTh
                  label="Market Cap"
                  sortKey="marketCapCr"
                  subLabel="Cr"
                />

                {/* Industry */}
                <th
                  onClick={() =>
                    setSortConfig((prev) => ({
                      key: "industry",
                      direction:
                        prev.key === "industry" &&
                        prev.direction === "desc"
                          ? "asc"
                          : "desc",
                    }))
                  }
                  className="cursor-pointer select-none text-left px-2 py-2"
                >
                  <span className="inline-flex items-center gap-1 text-blue-600">
                    Industry

                    {sortConfig.key === "industry" &&
                      (sortConfig.direction === "asc"
                        ? "↑"
                        : "↓")}
                  </span>
                </th>

                {/* 50 DMA */}
                <SortableTh
                  label="50 DMA"
                  sortKey="fiftyDayAverage"
                />

                {/* Diff */}
                <SortableTh
                  label="Diff"
                  sortKey="fiftyDayAverage_diff_per"
                  subLabel="vs 50 DMA"
                />

                {/* Fut OI */}
                <SortableTh
                  label="Fut"
                  sortKey="oi"
                  subLabel="OI%"
                />

                {/* Fut OI Change */}
                <SortableTh
                  label="Fut OI%"
                  sortKey="fut_oi_chg_20_med"
                  subLabel="Chg"
                />

                {/* PCR */}
                <SortableTh
                  label="PCR"
                  sortKey="pcr"
                />

                {/* PCR Change */}
                <SortableTh
                  label="PCR"
                  sortKey="op_pcr_chg_oi_20_avg"
                  subLabel="Chg"
                />

                {/* Margin Volume - NEW */}
                <SortableTh
                  label="Margin"
                  sortKey="margin_vol"
                  subLabel="Volume"
                />

                {/* Margin Volume Change - NEW */}
                <SortableTh
                  label="Margin Vol"
                  sortKey="margin_volChg20"
                  subLabel="Chg (20D)"
                />

              </tr>
            </thead>

            {/* ------------------------------------------------- */}
            {/* BODY */}
            {/* ------------------------------------------------- */}

            <tbody>
              {sortedRows.map((r, idx) => (

                <tr
                  key={r.symbol || idx}
                  className={`group ${
                    idx % 2 === 0
                      ? "bg-white"
                      : "bg-gray-50"
                  } hover:bg-blue-50`}
                >

                  {/* ------------------------------------------- */}
                  {/* NAME */}
                  {/* ------------------------------------------- */}

                  <td
                    className={`sticky left-0 z-20 border-r px-2 sm:px-3 py-2 font-medium
                      ${
                        idx % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50"
                      }
                      group-hover:bg-blue-50`}
                  >
                    <a
                      href={`/symbol/${r.symbol}`}
                      target="_blank"
                      rel="noreferrer"
                      title={r.name || r.symbol}
                      className="block hover:underline text-blue-600"
                    >

                      {/* Mobile */}
                      <span className="block sm:hidden truncate max-w-[10.5rem]">
                        {r.name || r.symbol}
                      </span>

                      {/* Desktop */}
                      <span className="hidden sm:block">
                        {r.name || r.symbol}
                      </span>

                      {/* Mobile Symbol */}
                      <span className="block sm:hidden text-[10px] text-gray-500">
                        {r.symbol}
                      </span>

                    </a>
                  </td>

                  {/* ------------------------------------------- */}
                  {/* PRICE */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {fmt(r.currentPrice, 2)}
                  </td>

                  {/* Price Change */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.chg} />
                  </td>

                  {/* ------------------------------------------- */}
                  {/* MARKET CAP */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {toNum(r.marketCapCr) !== null
                      ? Math.round(
                          r.marketCapCr
                        ).toLocaleString("en-IN")
                      : "—"}
                  </td>

                  {/* ------------------------------------------- */}
                  {/* INDUSTRY */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 py-2">
                    {showIndustry(r.industry)}
                  </td>

                  {/* ------------------------------------------- */}
                  {/* 50 DMA */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {fmt(r.fiftyDayAverage)}
                  </td>

                  {/* Diff vs 50 DMA */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ColorChange
                      value={r.fiftyDayAverage_diff_per}
                    />
                  </td>

                  {/* ------------------------------------------- */}
                  {/* FUTURES OI */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {r.oi != 0
                      ? fmt(r.oi, 2)
                      : "—"}
                  </td>

                  {/* Futures OI Change */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ColorChange
                      value={r.fut_oi_chg_20_med}
                    />
                  </td>

                  {/* ------------------------------------------- */}
                  {/* PCR */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {fmt(r.pcr, 2)}
                  </td>

                  {/* PCR Change */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ColorChange
                      value={r.op_pcr_chg_oi_20_avg}
                    />
                  </td>

                  {/* ------------------------------------------- */}
                  {/* MARGIN VOLUME - NEW */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {fmt(r.margin_vol, 0)}
                  </td>

                  {/* Margin Volume Change vs 20 DMA */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ColorChange
                      value={r.margin_volChg20}
                    />
                  </td>

                </tr>

              ))}
            </tbody>

          </table>
        </div>
      </div>
    );
  };

  const renderPriceDeliveryATS = () => {
    const arr = Array.isArray(technicalsData)
      ? technicalsData
      : Object.values(technicalsData || {});

    if (!arr.length) {
      return (
        <div className="text-sm text-red-500">
          No data available at the moment
        </div>
      );
    }

    // ---------------------------------------------------------
    // Sorting
    // ---------------------------------------------------------

    const sortRows = (rows) => {
      const { key, direction } = sortConfig;
      const dir = direction === "asc" ? 1 : -1;

      return [...rows].sort((a, b) => {
        const av = toNum(a?.[key]);
        const bv = toNum(b?.[key]);

        // Missing values always at bottom
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;

        return (av - bv) * dir;
      });
    };

    // ---------------------------------------------------------
    // Prepare Rows
    // ---------------------------------------------------------

    const rows = arr
      .filter((x) => {
        const vol = toNum(x.volume);

        if (vol === null || vol === 0) {
          return false;
        }

        // Show only symbols without names
        if (showMissingNamesOnly) {
          return x.symbol && !String(x.name || "").trim();
        }

        return true;
      })
      .map((x) => {
        const price = x.currentPrice;
        const prev = x.previousClose;

        return {
          symbol: x.symbol,
          name: x.name,

          // Price
          price,
          chg: pct(price, prev),

          // Volume
          volume: x.volume,
          volumeChg20: pct(x.volume, x.volume_20_avg),

          // Delivery
          delivery: x.delivery,
          deliveryChg20: pct(x.delivery, x.delivery_20_avg),
          deliveryPc: x.delivery_pc,

          // ATS
          ats: x.ats,
          atsChg20: pct(x.ats, x.ats_20_avg),

          // Margin Volume
          margin_vol: x.margin_vol,

          // Margin Volume vs 20 DMA
          margin_volChg20: pct(
            x.margin_vol,
            x.margin_vol_20_dma
          ),

          // Margin Value / Market Cap %
          margin_val_mcap_pct:
            toNum(x.marketCap) > 0 && toNum(x.margin_val) !== null
              ? (toNum(x.margin_val) / toNum(x.marketCap)) * 100
              : null,
        };
      });

    const sortedRows = sortRows(rows);

    const lastUpdateDate = arr[0]?.lastUpdateDate ?? null;

    if (!sortedRows.length) {
      return (
        <div className="text-sm text-red-500">
          No data available at the moment
        </div>
      );
    }

    const rowCount = sortedRows.length;

    return (
      <div>

        {/* ----------------------------------------------------- */}
        {/* Last Updated */}
        {/* ----------------------------------------------------- */}

        <div className="mt-2 text-gray-500">
          Updates daily 11 PM. Last updated -{" "}
          {formatDate(lastUpdateDate)}
        </div>

        {/* ----------------------------------------------------- */}
        {/* Row Count + Missing Names */}
        {/* ----------------------------------------------------- */}

        <div className="flex items-center justify-between mb-2">

          <div className="text-xs sm:text-sm text-gray-400">
            Tracked Symbols - {rowCount}
          </div>

          <button
            onClick={() =>
              setShowMissingNamesOnly((prev) => !prev)
            }
            className={`text-xs px-3 py-1 rounded border ${
              showMissingNamesOnly
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {showMissingNamesOnly ? "Show All" : "Missing Names"}
          </button>

        </div>

        {/* ----------------------------------------------------- */}
        {/* Table */}
        {/* ----------------------------------------------------- */}

        <div className="overflow-x-auto">

          <table className="table-fixed border-collapse text-xs sm:text-sm text-gray-800 min-w-[1150px] w-full">

            <colgroup>
              {/* Name */}
              <col className="w-44 sm:w-72" />

              {/* Price + Change */}
              <col className="w-24" />
              <col className="w-24" />

              {/* Volume + Change */}
              <col className="w-28" />
              <col className="w-32" />

              {/* Delivery + Change + % */}
              <col className="w-28" />
              <col className="w-36" />
              <col className="w-28" />

              {/* ATS + Change */}
              <col className="w-28" />
              <col className="w-32" />

              {/* Margin Volume + Change + Margin/MCap */}
              <col className="w-28" />
              <col className="w-32" />
              <col className="w-32" />
            </colgroup>

            {/* ------------------------------------------------- */}
            {/* Table Header */}
            {/* ------------------------------------------------- */}

            <thead className="bg-gray-50 text-gray-600">

              <tr>

                <th className="text-left px-2 sm:px-3 py-2 sticky left-0 z-30 bg-gray-50 border-r">
                  Name
                </th>

                {/* Price */}

                <SortableTh
                  label="Price"
                  sortKey="price"
                />

                <SortableTh
                  label="Price Chg"
                  sortKey="chg"
                  subLabel="(1D)"
                />

                {/* Volume */}

                <SortableTh
                  label="Volume"
                  sortKey="volume"
                />

                <SortableTh
                  label="Vol Chg"
                  sortKey="volumeChg20"
                  subLabel="(Avg - 20D)"
                />

                {/* Delivery */}

                <SortableTh
                  label="Del Vol"
                  sortKey="delivery"
                />

                <SortableTh
                  label="Del Vol Chg"
                  sortKey="deliveryChg20"
                  subLabel="(Avg - 20D)"
                />

                <SortableTh
                  label="Delivery %"
                  sortKey="deliveryPc"
                />

                {/* ATS */}

                <SortableTh
                  label="ATS"
                  sortKey="ats"
                />

                <SortableTh
                  label="ATS Chg"
                  sortKey="atsChg20"
                  subLabel="(Avg - 20D)"
                />

                {/* Margin Volume */}

                <SortableTh
                  label="Margin Volume"
                  sortKey="margin_vol"
                />

                <SortableTh
                  label="Margin Vol Chg"
                  sortKey="margin_volChg20"
                  subLabel="(Avg - 20D)"
                />

                <SortableTh
                  label="Margin / MCap"
                  sortKey="margin_val_mcap_pct"
                  subLabel="(%)"
                />

              </tr>

            </thead>

            {/* ------------------------------------------------- */}
            {/* Table Body */}
            {/* ------------------------------------------------- */}

            <tbody>

              {sortedRows.map((r, idx) => (

                <tr
                  key={r.symbol || idx}
                  className={`group ${
                    idx % 2 === 0
                      ? "bg-white"
                      : "bg-gray-50"
                  } hover:bg-blue-50`}
                >

                  {/* ------------------------------------------- */}
                  {/* Company Name */}
                  {/* ------------------------------------------- */}

                  <td
                    className={`px-2 sm:px-3 py-2 font-medium sticky left-0 z-20 border-r
                      ${
                        idx % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50"
                      }
                      group-hover:bg-blue-50`}
                  >

                    <a
                      href={`/symbol/${r.symbol}`}
                      target="_blank"
                      rel="noreferrer"
                      title={r.name || r.symbol}
                      className={`block hover:underline ${
                        r.name
                          ? "text-blue-600"
                          : "text-black"
                      }`}
                    >

                      {/* Mobile */}

                      <span className="block sm:hidden truncate max-w-[10.5rem]">
                        {r.name || r.symbol}
                      </span>

                      {/* Desktop */}

                      <span className="hidden sm:block text-blue-600">
                        {r.name || r.symbol}
                      </span>

                      {/* Mobile Symbol */}

                      <span className="block sm:hidden text-[10px] text-gray-500">
                        {r.symbol}
                      </span>

                    </a>

                  </td>

                  {/* ------------------------------------------- */}
                  {/* Price */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    {fmt(r.price, 2)}
                  </td>

                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.chg} />
                  </td>

                  {/* ------------------------------------------- */}
                  {/* Volume */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    {fmt(r.volume, 0)}
                  </td>

                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.volumeChg20} />
                  </td>

                  {/* ------------------------------------------- */}
                  {/* Delivery */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    {toNum(r.delivery) === 0
                      ? "—"
                      : fmt(r.delivery, 0)}
                  </td>

                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.deliveryChg20} />
                  </td>

                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    {fmt(r.deliveryPc, 2)}
                  </td>

                  {/* ------------------------------------------- */}
                  {/* ATS */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    {fmt(r.ats, 0)}
                  </td>

                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.atsChg20} />
                  </td>

                  {/* ------------------------------------------- */}
                  {/* Margin Volume */}
                  {/* ------------------------------------------- */}

                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    {fmt(r.margin_vol, 0)}
                  </td>

                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.margin_volChg20} />
                  </td>

                  {/* Margin Value / Market Cap % */}
                  <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">
                    {Number.isFinite(r.margin_val_mcap_pct)
                      ? `${r.margin_val_mcap_pct.toFixed(3)}%`
                      : "—"}
                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>
    );
  };

  const renderPrice52WHL_SMA = () => {
    const arr = Array.isArray(technicalsData)
      ? technicalsData
      : Object.values(technicalsData || {});

    if (!arr.length) {
      return (
        <div className="text-sm text-red-500">
          No data available at the moment
        </div>
      );
    }

    // ============================================================
    // FILTER CONFIG
    // ============================================================

    const filterKeys = [
      "chg",
      "marketCapCr",
      "fiftyTwoWeekHigh_diff_per",
      "fiftyDayAverage_diff_per",
      "twoHundredDayAverage_diff_per",
    ];

    const getEmptyAppliedFilters = () => ({
      chg: { op: ">", value: "" },
      marketCapCr: { op: ">", value: "" },
      fiftyTwoWeekHigh_diff_per: { op: ">", value: "" },
      fiftyDayAverage_diff_per: { op: ">", value: "" },
      twoHundredDayAverage_diff_per: { op: ">", value: "" },
    });

    // ============================================================
    // APPLY FILTERS
    //
    // Reads directly from HTML input/select refs.
    // This is the ONLY time we update filter React state.
    // ============================================================

    const applyFilters = () => {
      const refs = price52FilterRefs.current;

      setAppliedPrice52Filters({
        chg: {
          op: refs.chg_op?.value || ">",
          value: refs.chg_value?.value || "",
        },

        marketCapCr: {
          op: refs.marketCapCr_op?.value || ">",
          value: refs.marketCapCr_value?.value || "",
        },

        fiftyTwoWeekHigh_diff_per: {
          op:
            refs.fiftyTwoWeekHigh_diff_per_op?.value ||
            ">",
          value:
            refs.fiftyTwoWeekHigh_diff_per_value?.value ||
            "",
        },

        fiftyDayAverage_diff_per: {
          op:
            refs.fiftyDayAverage_diff_per_op?.value ||
            ">",
          value:
            refs.fiftyDayAverage_diff_per_value?.value ||
            "",
        },

        twoHundredDayAverage_diff_per: {
          op:
            refs.twoHundredDayAverage_diff_per_op?.value ||
            ">",
          value:
            refs.twoHundredDayAverage_diff_per_value?.value ||
            "",
        },
      });
    };

    // ============================================================
    // CLEAR FILTERS
    //
    // Reset HTML controls directly.
    // Then clear applied React state once.
    // ============================================================

    const clearFilters = () => {
      const refs = price52FilterRefs.current;

      filterKeys.forEach((key) => {
        const opRef = refs[`${key}_op`];
        const valueRef = refs[`${key}_value`];

        if (opRef) {
          opRef.value = ">";
        }

        if (valueRef) {
          valueRef.value = "";
        }
      });

      setAppliedPrice52Filters(
        getEmptyAppliedFilters()
      );
    };

    // ============================================================
    // SINGLE FILTER CHECK
    // ============================================================

    const passesFilter = (rowValue, filter) => {
      if (
        filter?.value === "" ||
        filter?.value === null ||
        filter?.value === undefined
      ) {
        return true;
      }

      const rowNum = toNum(rowValue);
      const filterNum = toNum(filter.value);

      if (
        rowNum === null ||
        filterNum === null
      ) {
        return false;
      }

      if (filter.op === "<") {
        return rowNum < filterNum;
      }

      return rowNum > filterNum;
    };

    // ============================================================
    // IS ANY FILTER CURRENTLY APPLIED?
    // ============================================================

    const hasAppliedFilter = Object.values(
      appliedPrice52Filters
    ).some(
      (filter) =>
        filter?.value !== "" &&
        filter?.value !== null &&
        filter?.value !== undefined
    );

    // ============================================================
    // FILTER CONTROL
    //
    // IMPORTANT:
    //
    // UNCONTROLLED inputs.
    //
    // No value=
    // No onChange=
    // No setState while typing.
    // ============================================================

    const renderFilterControl = (
      label,
      filterKey,
      placeholder = "%",
      inputWidth = "w-20"
    ) => {
      return (
        <div
          key={filterKey}
          className="flex flex-col gap-1 flex-none"
        >
          <div className="text-xs font-semibold text-gray-700 whitespace-nowrap">
            {label}
          </div>

          <div className="flex items-center gap-1">

            {/* ================================ */}
            {/* > / < DROPDOWN */}
            {/* ================================ */}

            <select
              defaultValue={
                appliedPrice52Filters?.[filterKey]?.op ||
                ">"
              }
              ref={(el) => {
                price52FilterRefs.current[
                  `${filterKey}_op`
                ] = el;
              }}
              className="
                h-8
                w-12
                px-1
                border
                border-gray-300
                rounded
                bg-white
                text-sm
                font-semibold
                text-gray-700
                focus:outline-none
                focus:ring-1
                focus:ring-blue-500
              "
            >
              <option value=">">&gt;</option>
              <option value="<">&lt;</option>
            </select>


            {/* ================================ */}
            {/* VALUE */}
            {/* ================================ */}

            <div className="relative">

              <input
                type="number"
                step="any"
                defaultValue={
                  appliedPrice52Filters?.[filterKey]
                    ?.value || ""
                }
                ref={(el) => {
                  price52FilterRefs.current[
                    `${filterKey}_value`
                  ] = el;
                }}
                placeholder={placeholder}
                className={`
                  h-8
                  ${inputWidth}
                  border
                  border-gray-300
                  rounded
                  px-2
                  pr-6
                  text-sm
                  bg-white
                  text-gray-700
                  focus:outline-none
                  focus:ring-1
                  focus:ring-blue-500
                `}
              />


              {/* ============================== */}
              {/* CLEAR ONLY THIS INPUT */}
              {/* ============================== */}

              <button
                type="button"
                onClick={() => {
                  const ref =
                    price52FilterRefs.current[
                      `${filterKey}_value`
                    ];

                  if (ref) {
                    ref.value = "";
                    ref.focus();
                  }
                }}
                className="
                  absolute
                  right-1.5
                  top-1/2
                  -translate-y-1/2
                  text-gray-400
                  hover:text-red-500
                  text-sm
                "
                title={`Clear ${label}`}
              >
                ×
              </button>

            </div>

          </div>
        </div>
      );
    };

    // ============================================================
    // PREPARE ROWS
    // ============================================================

    const rows = arr
      .filter((x) => {
        if (
          toNum(x.currentPrice) === null
        ) {
          return false;
        }

        if (showMissingNamesOnly) {
          return (
            x.symbol &&
            !String(
              x.name || ""
            ).trim()
          );
        }

        return true;
      })
      .map((x) => ({
        ...x,

        price: x.currentPrice,

        // Price change %
        chg: pct(
          x.currentPrice,
          x.previousClose
        ),

        // Market Cap Rs -> Crore
        marketCapCr:
          toNum(x.marketCap) !== null
            ? toNum(x.marketCap) /
              10000000
            : null,

        // Price vs 52W High
        fiftyTwoWeekHigh_diff_per:
          pct(
            x.currentPrice,
            x.fiftyTwoWeekHigh
          ),

        // Price vs 50 DMA
        fiftyDayAverage_diff_per:
          pct(
            x.currentPrice,
            x.fiftyDayAverage
          ),

        // Price vs 200 DMA
        twoHundredDayAverage_diff_per:
          pct(
            x.currentPrice,
            x.twoHundredDayAverage
          ),
      }));

    // ============================================================
    // FILTER ROWS
    //
    // IMPORTANT:
    //
    // Only appliedPrice52Filters are used.
    //
    // Changing textbox/dropdown does NOTHING here.
    // Clicking APPLY changes appliedPrice52Filters.
    // ============================================================

    const filteredRows = rows.filter((r) => {
      return (
        passesFilter(
          r.chg,
          appliedPrice52Filters.chg
        ) &&

        passesFilter(
          r.marketCapCr,
          appliedPrice52Filters.marketCapCr
        ) &&

        passesFilter(
          r.fiftyTwoWeekHigh_diff_per,
          appliedPrice52Filters
            .fiftyTwoWeekHigh_diff_per
        ) &&

        passesFilter(
          r.fiftyDayAverage_diff_per,
          appliedPrice52Filters
            .fiftyDayAverage_diff_per
        ) &&

        passesFilter(
          r.twoHundredDayAverage_diff_per,
          appliedPrice52Filters
            .twoHundredDayAverage_diff_per
        )
      );
    });

    // ============================================================
    // SORT
    // ============================================================

    const sortedRows = [...filteredRows].sort(
      (a, b) => {
        const dir =
          sortConfig.direction === "asc"
            ? 1
            : -1;

        const key = sortConfig.key;

        if (key === "industry") {
          return (
            String(
              a.industry || ""
            ).localeCompare(
              String(
                b.industry || ""
              )
            ) * dir
          );
        }

        const av = toNum(a[key]);
        const bv = toNum(b[key]);

        if (
          av === null &&
          bv === null
        ) {
          return 0;
        }

        if (av === null) {
          return 1;
        }

        if (bv === null) {
          return -1;
        }

        return (av - bv) * dir;
      }
    );

    // ============================================================
    // INDUSTRY DISPLAY
    // ============================================================

    const showIndustry = (industry) => {
      if (
        !industry ||
        ["N", "NEW"].includes(
          String(industry)
            .trim()
            .toUpperCase()
        )
      ) {
        return "—";
      }

      return String(industry)
        .split("/")
        .map((x, i) => (
          <div
            key={i}
            className="
              block
              w-fit
              bg-gray-100
              text-gray-600
              text-xs
              px-2
              py-0.5
              rounded-full
              mb-1
            "
          >
            {x.trim()}
          </div>
        ));
    };

    // ============================================================
    // CSV HELPERS
    // ============================================================

    const escapeCsv = (value) => {
      if (
        value === null ||
        value === undefined
      ) {
        return "";
      }

      const str = String(value);

      if (/[",\n]/.test(str)) {
        return `"${str.replace(
          /"/g,
          '""'
        )}"`;
      }

      return str;
    };

    // ============================================================
    // DOWNLOAD CSV
    //
    // ALWAYS uses sortedRows.
    //
    // Therefore:
    // table rows === tracked count === CSV rows
    // ============================================================

    const downloadCsv = () => {
      if (!sortedRows.length) {
        return;
      }

      const csvRows = sortedRows.map(
        (r) => ({
          Symbol:
            r.symbol || "",

          Name:
            r.name || "",

          Price:
            toNum(r.price) !== null
              ? Number(
                  r.price
                ).toFixed(2)
              : "",

          "Price Chg %":
            toNum(r.chg) !== null
              ? Number(
                  r.chg
                ).toFixed(2)
              : "",

          "Market Cap Cr":
            toNum(
              r.marketCapCr
            ) !== null
              ? Math.round(
                  r.marketCapCr
                )
              : "",

          Industry:
            r.industry || "",

          "52W High":
            toNum(
              r.fiftyTwoWeekHigh
            ) !== null
              ? Number(
                  r.fiftyTwoWeekHigh
                ).toFixed(2)
              : "",

          "Diff vs 52W High %":
            toNum(
              r.fiftyTwoWeekHigh_diff_per
            ) !== null
              ? Number(
                  r.fiftyTwoWeekHigh_diff_per
                ).toFixed(2)
              : "",

          "50 DMA":
            toNum(
              r.fiftyDayAverage
            ) !== null
              ? Number(
                  r.fiftyDayAverage
                ).toFixed(2)
              : "",

          "Diff vs 50 DMA %":
            toNum(
              r.fiftyDayAverage_diff_per
            ) !== null
              ? Number(
                  r.fiftyDayAverage_diff_per
                ).toFixed(2)
              : "",

          "200 DMA":
            toNum(
              r.twoHundredDayAverage
            ) !== null
              ? Number(
                  r.twoHundredDayAverage
                ).toFixed(2)
              : "",

          "Diff vs 200 DMA %":
            toNum(
              r.twoHundredDayAverage_diff_per
            ) !== null
              ? Number(
                  r.twoHundredDayAverage_diff_per
                ).toFixed(2)
              : "",

          "Last 10D > 2%":
            toNum(r.last10days_morethan_2pc) !== null
              ? Number(r.last10days_morethan_2pc)
              : "",
        })
      );

      const columns =
        Object.keys(csvRows[0]);

      const header =
        columns
          .map(escapeCsv)
          .join(",");

      const body =
        csvRows
          .map((row) =>
            columns
              .map((col) =>
                escapeCsv(
                  row[col]
                )
              )
              .join(",")
          )
          .join("\n");

      const csv =
        "\uFEFF" +
        header +
        "\n" +
        body;

      const blob =
        new Blob(
          [csv],
          {
            type:
              "text/csv;charset=utf-8;",
          }
        );

      const url =
        URL.createObjectURL(blob);

      const a =
        document.createElement("a");

      a.href = url;

      a.download =
        "price_52w_high_low_sma.csv";

      document.body.appendChild(a);

      a.click();

      a.remove();

      URL.revokeObjectURL(url);
    };

    // ============================================================
    // OTHER
    // ============================================================

    const lastUpdateDate =
      arr[0]?.lastUpdateDate;

    // ============================================================
    // UI
    // ============================================================

    return (
      <div>

        {/* ================================================ */}
        {/* STATUS */}
        {/* ================================================ */}

        <div className="mt-2 text-gray-500">
          Updates daily 11 PM. Last updated -{" "}
          {formatDate(
            lastUpdateDate
          )}
        </div>


        {/* ================================================ */}
        {/* FILTERS */}
        {/* ================================================ */}

        <div className="mt-3 mb-3 border rounded bg-gray-50 p-3">

          <div className="overflow-x-auto">

            <div className="flex items-end gap-4 min-w-max">


              {/* PRICE CHANGE */}
              {renderFilterControl(
                "Price Chg",
                "chg",
                "%"
              )}


              {/* MARKET CAP */}
              {renderFilterControl(
                "Market Cap Cr",
                "marketCapCr",
                "Cr",
                "w-24"
              )}


              {/* 52W HIGH */}
              {renderFilterControl(
                "Diff vs 52W High",
                "fiftyTwoWeekHigh_diff_per",
                "%"
              )}


              {/* 50 DMA */}
              {renderFilterControl(
                "Diff vs 50 DMA",
                "fiftyDayAverage_diff_per",
                "%"
              )}


              {/* 200 DMA */}
              {renderFilterControl(
                "Diff vs 200 DMA",
                "twoHundredDayAverage_diff_per",
                "%"
              )}


              {/* ========================================== */}
              {/* APPLY */}
              {/* ========================================== */}

              <button
                type="button"
                onClick={
                  applyFilters
                }
                className="
                  h-8
                  px-5
                  flex-none
                  rounded
                  border
                  border-blue-600
                  bg-blue-600
                  text-white
                  text-xs
                  font-semibold
                  hover:bg-blue-700
                "
              >
                Apply
              </button>


              {/* ========================================== */}
              {/* CLEAR */}
              {/* ========================================== */}

              <button
                type="button"
                onClick={
                  clearFilters
                }
                className="
                  h-8
                  px-5
                  flex-none
                  rounded
                  border
                  border-gray-300
                  bg-white
                  text-gray-600
                  text-xs
                  font-semibold
                  hover:bg-gray-100
                "
              >
                Clear
              </button>

            </div>

          </div>

        </div>


        {/* ================================================ */}
        {/* COUNT + ACTIONS */}
        {/* ================================================ */}

        <div className="flex items-center justify-between mb-2">

          {/* FILTERED COUNT */}

          <div className="text-xs sm:text-sm text-gray-400">

            Tracked Symbols -{" "}
            {sortedRows.length}

            {hasAppliedFilter && (
              <span className="ml-2">
                (Filtered from{" "}
                {rows.length})
              </span>
            )}

          </div>


          <div className="flex items-center gap-2">


            {/* ========================================== */}
            {/* MISSING NAMES */}
            {/* ========================================== */}

            <button
              type="button"
              onClick={() =>
                setShowMissingNamesOnly(
                  (prev) =>
                    !prev
                )
              }
              className={`text-xs px-3 py-1 rounded border ${
                showMissingNamesOnly
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {showMissingNamesOnly
                ? "Show All"
                : "Missing Names"}
            </button>


            {/* ========================================== */}
            {/* DOWNLOAD FILTERED CSV */}
            {/* ========================================== */}

            <button
              type="button"
              onClick={
                downloadCsv
              }
              disabled={
                !sortedRows.length
              }
              className={`text-xs px-3 py-1 rounded border ${
                sortedRows.length
                  ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
                  : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
              }`}
            >
              Download CSV
            </button>

          </div>

        </div>


        {/* ================================================ */}
        {/* TABLE */}
        {/* ================================================ */}

        <div className="overflow-x-auto">

          <table className="table-fixed border-collapse text-xs sm:text-sm text-gray-800 min-w-[1250px] w-full">

            <colgroup>

              {/* Name */}
              <col className="w-44 sm:w-72" />

              {/* Price */}
              <col className="w-24" />

              {/* Chg */}
              <col className="w-24" />

              {/* Market Cap */}
              <col className="w-24" />

              {/* Industry */}
              <col className="w-36" />

              {/* 52W */}
              <col className="w-28" />

              {/* Diff 52W */}
              <col className="w-28" />

              {/* 50 DMA */}
              <col className="w-28" />

              {/* Diff 50 */}
              <col className="w-28" />

              {/* 200 DMA */}
              <col className="w-28" />

              {/* Diff 200 */}
              <col className="w-28" />

              {/* Last 10 Days > 2% */}
              <col className="w-14" />

            </colgroup>


            {/* ============================================== */}
            {/* HEADER */}
            {/* ============================================== */}

            <thead className="bg-gray-50 text-gray-600">

              <tr>


                {/* NAME */}

                <th className="text-left px-3 py-2 sticky left-0 z-30 bg-gray-50 border-r">
                  Name
                </th>


                {/* PRICE */}

                <th className="text-right px-3 py-2">
                  Price
                </th>


                {/* PRICE CHANGE */}

                <SortableTh
                  label="Price"
                  sortKey="chg"
                  subLabel="Chg"
                />


                {/* MARKET CAP */}

                <SortableTh
                  label="Market Cap"
                  sortKey="marketCapCr"
                  subLabel="Cr"
                />


                {/* INDUSTRY */}

                <th
                  onClick={() =>
                    setSortConfig(
                      (prev) => ({
                        key:
                          "industry",

                        direction:
                          prev.key ===
                            "industry" &&
                          prev.direction ===
                            "desc"
                            ? "asc"
                            : "desc",
                      })
                    )
                  }
                  className="cursor-pointer select-none text-left px-3 py-2"
                >

                  <span className="inline-flex items-center gap-1 text-blue-600">

                    Industry

                    {sortConfig.key ===
                      "industry" &&
                      (sortConfig.direction ===
                      "asc"
                        ? "↑"
                        : "↓")}

                  </span>

                </th>


                {/* 52W HIGH */}

                <SortableTh
                  label="52W High"
                  sortKey="fiftyTwoWeekHigh"
                />


                {/* DIFF 52W */}

                <SortableTh
                  label="Diff"
                  sortKey="fiftyTwoWeekHigh_diff_per"
                  subLabel="vs 52W High"
                />


                {/* 50 DMA */}

                <SortableTh
                  label="50 DMA"
                  sortKey="fiftyDayAverage"
                />


                {/* DIFF 50 */}

                <SortableTh
                  label="Diff"
                  sortKey="fiftyDayAverage_diff_per"
                  subLabel="vs 50 DMA"
                />


                {/* 200 DMA */}

                <SortableTh
                  label="200 DMA"
                  sortKey="twoHundredDayAverage"
                />


                {/* DIFF 200 */}

                <SortableTh
                  label="Diff"
                  sortKey="twoHundredDayAverage_diff_per"
                  subLabel="vs 200 DMA"
                />

                {/* LAST 10 DAYS > 2% */}

                <SortableTh
                  label="Last 10D"
                  sortKey="last10days_morethan_2pc"
                  subLabel="> 2%"
                />

              </tr>

            </thead>


            {/* ============================================== */}
            {/* BODY */}
            {/* ============================================== */}

            <tbody>

              {sortedRows.length ===
              0 ? (

                <tr>

                  <td
                    colSpan={11}
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    No stocks match the selected filters.
                  </td>

                </tr>

              ) : (

                sortedRows.map(
                  (r, idx) => (

                    <tr
                      key={
                        r.symbol ||
                        idx
                      }
                      className={`group ${
                        idx % 2 ===
                        0
                          ? "bg-white"
                          : "bg-gray-50"
                      } hover:bg-blue-50`}
                    >


                      {/* ======================================== */}
                      {/* NAME */}
                      {/* ======================================== */}

                      <td
                        className={`px-3 py-2 font-medium sticky left-0 z-20 border-r
                          ${
                            idx %
                              2 ===
                            0
                              ? "bg-white"
                              : "bg-gray-50"
                          }
                          group-hover:bg-blue-50
                        `}
                      >

                        <a
                          href={`/symbol/${r.symbol}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline"
                        >

                          {/* Mobile */}

                          <span className="block sm:hidden truncate max-w-[10.5rem]">
                            {r.name ||
                              r.symbol}
                          </span>


                          {/* Desktop */}

                          <span className="hidden sm:block">
                            {r.name ||
                              r.symbol}
                          </span>


                          {/* Mobile Symbol */}

                          <span className="block sm:hidden text-[10px] text-gray-500">
                            {r.symbol}
                          </span>

                        </a>

                      </td>


                      {/* ======================================== */}
                      {/* PRICE */}
                      {/* ======================================== */}

                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {fmt(
                          r.price
                        )}
                      </td>


                      {/* ======================================== */}
                      {/* PRICE CHANGE */}
                      {/* ======================================== */}

                      <td className="px-3 py-2 text-right whitespace-nowrap">

                        <ColorChange
                          value={
                            r.chg
                          }
                        />

                      </td>


                      {/* ======================================== */}
                      {/* MARKET CAP */}
                      {/* ======================================== */}

                      <td className="px-3 py-2 text-right whitespace-nowrap">

                        {toNum(
                          r.marketCapCr
                        ) !== null
                          ? Math.round(
                              r.marketCapCr
                            ).toLocaleString(
                              "en-IN"
                            )
                          : "—"}

                      </td>


                      {/* ======================================== */}
                      {/* INDUSTRY */}
                      {/* ======================================== */}

                      <td className="px-3 py-2">

                        {showIndustry(
                          r.industry
                        )}

                      </td>


                      {/* ======================================== */}
                      {/* 52 WEEK HIGH */}
                      {/* ======================================== */}

                      <td className="px-3 py-2 text-right whitespace-nowrap">

                        {fmt(
                          r.fiftyTwoWeekHigh
                        )}

                      </td>


                      {/* DIFF 52W */}

                      <td className="px-3 py-2 text-right whitespace-nowrap">

                        <ColorChange
                          value={
                            r.fiftyTwoWeekHigh_diff_per
                          }
                        />

                      </td>


                      {/* ======================================== */}
                      {/* 50 DMA */}
                      {/* ======================================== */}

                      <td className="px-3 py-2 text-right whitespace-nowrap">

                        {fmt(
                          r.fiftyDayAverage
                        )}

                      </td>


                      {/* DIFF 50 DMA */}

                      <td className="px-3 py-2 text-right whitespace-nowrap">

                        <ColorChange
                          value={
                            r.fiftyDayAverage_diff_per
                          }
                        />

                      </td>


                      {/* ======================================== */}
                      {/* 200 DMA */}
                      {/* ======================================== */}

                      <td className="px-3 py-2 text-right whitespace-nowrap">

                        {fmt(
                          r.twoHundredDayAverage
                        )}

                      </td>


                      {/* DIFF 200 DMA */}

                      <td className="px-3 py-2 text-right whitespace-nowrap">

                        <ColorChange
                          value={
                            r.twoHundredDayAverage_diff_per
                          }
                        />

                      </td>

                      {/* LAST 10 DAYS > 2% */}

                      <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">
                        {toNum(r.last10days_morethan_2pc) !== null
                          ? r.last10days_morethan_2pc
                          : "—"}
                      </td>

                    </tr>
                  )
                )

              )}

            </tbody>

          </table>

        </div>

      </div>
    );
  };

  const renderDeepRecovery = () => {
    const arr = Array.isArray(technicalsData)
      ? technicalsData
      : Object.values(technicalsData || {});

    const rows = arr
      .filter((x) => Number(x.deep_recovery) === 1)
      .map((x) => ({
        symbol: x.symbol,
        name: x.name,
        industry: x.industry,

        price: x.currentPrice,
        chg: pct(x.currentPrice, x.previousClose),

        // marketCap is in Rs -> convert to Crore
        marketCapCr:
          toNum(x.marketCap) !== null
            ? toNum(x.marketCap) / 10000000
            : null,
      }))
      .sort((a, b) => {
        const dir = sortConfig.direction === "asc" ? 1 : -1;
        const key = sortConfig.key;

        // Industry alphabetical sorting
        if (key === "industry") {
          return (
            String(a.industry || "").localeCompare(
              String(b.industry || "")
            ) * dir
          );
        }

        const av = toNum(a[key]);
        const bv = toNum(b[key]);

        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;

        return (av - bv) * dir;
      });

    const showIndustry = (industry) => {
      if (
        !industry ||
        ["N", "NEW"].includes(String(industry).trim().toUpperCase())
      ) {
        return "—";
      }

      return String(industry)
        .split("/")
        .map((x, i) => (
          <div
            key={i}
            className="block w-fit bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full mb-1"
          >
            {x.trim()}
          </div>
        ));
    };

    if (!rows.length) {
      return (
        <div className="text-sm text-red-500">
          No deep recovery stocks found.
        </div>
      );
    }

    const lastUpdateDate = arr[0]?.lastUpdateDate;

    return (
      <div>
        <div className="mt-2 text-gray-500">
          Stocks that had a deep intraday fall followed by a strong recovery. Check Candle stick charts for deep wick.
          {lastUpdateDate && (
            <>
              {" "}Last updated - {formatDate(lastUpdateDate)}
            </>
          )}
        </div>

        <div className="text-xs sm:text-sm text-gray-400 mb-2">
          Stocks Found - {rows.length}
        </div>

        <div className="overflow-x-auto">
          <table className="table-fixed border-collapse text-xs sm:text-sm text-gray-800 min-w-[800px] w-full">

            <colgroup>
              <col className="w-44 sm:w-72" />
              <col className="w-24" />
              <col className="w-24" />
              <col className="w-28" />
              <col className="w-44" />
            </colgroup>

            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">
                  Name
                </th>

                <th className="text-right px-3 py-2">
                  Price
                </th>

                <SortableTh
                  label="Price"
                  sortKey="chg"
                  subLabel="Chg"
                />

                <SortableTh
                  label="Market Cap"
                  sortKey="marketCapCr"
                  subLabel="Cr"
                />

                <SortableTh
                  label="Industry"
                  sortKey="industry"
                  align="left"
                />
              </tr>
            </thead>

            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={r.symbol || idx}
                  className={`group ${
                    idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } hover:bg-blue-50`}
                >

                  {/* Name */}
                  <td className="px-3 py-2 font-medium">
                    <a
                      href={`/symbol/${r.symbol}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      <span className="block sm:hidden truncate max-w-[10.5rem]">
                        {r.name || r.symbol}
                      </span>

                      <span className="hidden sm:block">
                        {r.name || r.symbol}
                      </span>

                      <span className="block sm:hidden text-[10px] text-gray-500">
                        {r.symbol}
                      </span>
                    </a>
                  </td>

                  {/* Price */}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {fmt(r.price)}
                  </td>

                  {/* Price Change */}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.chg} />
                  </td>

                  {/* Market Cap */}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {toNum(r.marketCapCr) !== null
                      ? Math.round(r.marketCapCr).toLocaleString("en-IN")
                      : "—"}
                  </td>

                  {/* Industry */}
                  <td className="px-3 py-2">
                    {showIndustry(r.industry)}
                  </td>

                </tr>
              ))}
            </tbody>

          </table>
        </div>
      </div>
    );
  };

  const ipoTracking = () => {
    const arr = Array.isArray(ipoTrackingData)
      ? ipoTrackingData
      : Object.values(ipoTrackingData || {});

    if (!arr.length) {
      return (
        <div className="text-sm text-red-500">
          No data available at the moment
        </div>
      );
    }

    // ============================================================
    // PREPARE ROWS
    // ============================================================

    const rows = arr.map((x) => {
      const issuePrice = toNum(x.issuePrice);
      const issueSize = toNum(x.issueSize);
      const firstDayClosing = toNum(x.firstDayClosing);
      const currentPrice = toNum(x.currentPrice);
      const marketCap = toNum(x.marketCap);

      // IPO size = issue price × number of shares
      // Convert Rs to Crore
      const totalIpoSizeCr =
        issuePrice !== null && issueSize !== null
          ? (issuePrice * issueSize) / 10000000
          : null;

      // Current Market Cap
      // Input marketCap is in Rs -> convert to Crore
      const marketCapCr =
        marketCap !== null
          ? marketCap / 10000000
          : null;

      // First day gain vs IPO issue price
      const firstDayGain =
        issuePrice !== null &&
        firstDayClosing !== null &&
        issuePrice !== 0
          ? pct(firstDayClosing, issuePrice)
          : null;

      const firstDayPurchaseGain =
        firstDayClosing !== null &&
        currentPrice !== null &&
        firstDayClosing !== 0
          ? pct(currentPrice, firstDayClosing)
          : null;

      // Total gain vs IPO issue price
      const totalGain =
        issuePrice !== null &&
        currentPrice !== null &&
        issuePrice !== 0
          ? pct(currentPrice, issuePrice)
          : null;

      return {
        ...x,

        issuePrice,
        issueSize,
        firstDayClosing,
        currentPrice,
        marketCap,

        totalIpoSizeCr,
        marketCapCr,
        firstDayGain,
        firstDayPurchaseGain,
        totalGain,
      };
    });

    // ============================================================
    // SORTING
    // ============================================================

    const sortedRows = [...rows].sort((a, b) => {
      const { key, direction } = sortConfig;
      const dir = direction === "asc" ? 1 : -1;

      // Listing date
      if (key === "effectiveDate") {
        const av = new Date(a.effectiveDate).getTime();
        const bv = new Date(b.effectiveDate).getTime();

        if (!Number.isFinite(av) && !Number.isFinite(bv)) return 0;
        if (!Number.isFinite(av)) return 1;
        if (!Number.isFinite(bv)) return -1;

        return (av - bv) * dir;
      }

      // Name
      if (key === "companyName") {
        return (
          String(a.companyName || "").localeCompare(
            String(b.companyName || "")
          ) * dir
        );
      }

      // Industry
      if (key === "industry") {
        return (
          String(a.industry || "").localeCompare(
            String(b.industry || "")
          ) * dir
        );
      }

      // Numeric columns
      const av = toNum(a[key]);
      const bv = toNum(b[key]);

      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;

      return (av - bv) * dir;
    });

    const rowCount = sortedRows.length;

    // ============================================================
    // DATE FORMAT
    // ============================================================

    const formatListingDate = (value) => {
      if (!value) return "—";

      const d = new Date(value);

      if (Number.isNaN(d.getTime())) {
        return "—";
      }

      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    // ============================================================
    // TABLE
    // ============================================================

    return (
      <div>
        <div className="mt-2 text-gray-500">
          IPO performance from listing date to current price.
        </div>

        <div className="text-xs sm:text-sm text-gray-400 mb-2">
          Tracked IPOs - {rowCount}
        </div>

        <div className="overflow-x-auto">
          <table className="table-fixed border-collapse text-xs sm:text-sm text-gray-800 min-w-[1250px] w-full">

            {/* ================================================= */}
            {/* COLUMN WIDTHS */}
            {/* ================================================= */}

            <colgroup>
              {/* Name */}
              <col className="w-52 sm:w-80" />

              {/* Listing Date */}
              <col className="w-32" />

              {/* Industry */}
              <col className="w-40" />

              {/* IPO Price */}
              <col className="w-24" />

              {/* Current Price */}
              <col className="w-28" />

              {/* IPO Size */}
              <col className="w-28" />

              {/* Market Cap */}
              <col className="w-28" />

              {/* First Day Gain */}
              <col className="w-28" />

              {/* First Day Purchase Gain */}
              <col className="w-32" />

              {/* Total Gain */}
              <col className="w-28" />
            </colgroup>

            {/* ================================================= */}
            {/* HEADER */}
            {/* ================================================= */}

            <thead className="bg-gray-50 text-gray-600">
              <tr>

                {/* Name */}
                <th
                  onClick={() =>
                    setSortConfig((prev) => ({
                      key: "companyName",
                      direction:
                        prev.key === "companyName" &&
                        prev.direction === "desc"
                          ? "asc"
                          : "desc",
                    }))
                  }
                  className="
                    sticky left-0 z-30
                    bg-gray-50
                    border-r
                    px-2 sm:px-3 py-2
                    text-left
                    cursor-pointer
                    select-none
                  "
                >
                  <span className="inline-flex items-center gap-1 text-blue-600">
                    Name

                    {sortConfig.key === "companyName" &&
                      (sortConfig.direction === "asc"
                        ? "↑"
                        : "↓")}
                  </span>
                </th>

                {/* Listing Date */}
                <SortableTh
                  label="Listing"
                  subLabel="Date"
                  sortKey="effectiveDate"
                />

                {/* Industry */}
                <th
                  onClick={() =>
                    setSortConfig((prev) => ({
                      key: "industry",
                      direction:
                        prev.key === "industry" &&
                        prev.direction === "desc"
                          ? "asc"
                          : "desc",
                    }))
                  }
                  className="
                    cursor-pointer
                    select-none
                    px-3 py-2
                    text-left
                  "
                >
                  <span className="inline-flex items-center gap-1 text-blue-600">
                    Industry

                    {sortConfig.key === "industry" &&
                      (sortConfig.direction === "asc"
                        ? "↑"
                        : "↓")}
                  </span>
                </th>

                {/* IPO Price */}
                <SortableTh
                  label="IPO"
                  subLabel="Price"
                  sortKey="issuePrice"
                />

                {/* Current Price */}
                <SortableTh
                  label="Current"
                  subLabel="Price"
                  sortKey="currentPrice"
                />

                {/* IPO Size */}
                <SortableTh
                  label="IPO Size"
                  subLabel="Cr"
                  sortKey="totalIpoSizeCr"
                />

                {/* Market Cap */}
                <SortableTh
                  label="Market Cap"
                  subLabel="Cr"
                  sortKey="marketCapCr"
                />

                {/* First Day Gain */}
                <SortableTh
                  label="Listing Day"
                  subLabel="Gain"
                  sortKey="firstDayGain"
                />

                <SortableTh
                  label="Gain After"
                  subLabel="First Day Purchase"
                  sortKey="firstDayPurchaseGain"
                />

                {/* Total Gain */}
                <SortableTh
                  label="Total Holding"
                  subLabel="Gain"
                  sortKey="totalGain"
                />

              </tr>
            </thead>

            {/* ================================================= */}
            {/* BODY */}
            {/* ================================================= */}

            <tbody>
              {sortedRows.map((r, idx) => (
                <tr
                  key={`${r.symbol}-${idx}`}
                  className={`group ${
                    idx % 2 === 0
                      ? "bg-white"
                      : "bg-gray-50"
                  } hover:bg-blue-50`}
                >

                  {/* ================================================= */}
                  {/* NAME */}
                  {/* ================================================= */}

                  <td
                    className={`
                      sticky left-0 z-20
                      border-r
                      px-2 sm:px-3 py-2
                      font-medium
                      ${
                        idx % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50"
                      }
                      group-hover:bg-blue-50
                    `}
                  >
                    <a
                      href={`/symbol/${r.symbol}`}
                      target="_blank"
                      rel="noreferrer"
                      title={r.companyName || r.symbol}
                      className="block hover:underline text-blue-600"
                    >
                      {r.companyName || r.symbol}
                    </a>
                  </td>

                  {/* ================================================= */}
                  {/* LISTING DATE */}
                  {/* ================================================= */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {formatListingDate(r.effectiveDate)}
                  </td>

                  {/* ================================================= */}
                  {/* INDUSTRY */}
                  {/* ================================================= */}

                  <td className="px-2 py-2 text-left">
                    {r.industry &&
                    !["NEW", "N"].includes(String(r.industry).trim().toUpperCase()) ? (
                      String(r.industry)
                        .split("/")
                        .map((industry, i) => (
                          <div
                            key={i}
                            className="block w-fit bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full mb-1"
                          >
                            {industry.trim()}
                          </div>
                        ))
                    ) : (
                      "—"
                    )}
                  </td>

                  {/* ================================================= */}
                  {/* IPO PRICE */}
                  {/* ================================================= */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {r.issuePrice !== null
                      ? `₹${Number(r.issuePrice).toLocaleString(
                          "en-IN",
                          {
                            maximumFractionDigits: 2,
                          }
                        )}`
                      : "—"}
                  </td>

                  {/* ================================================= */}
                  {/* CURRENT PRICE */}
                  {/* ================================================= */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {r.currentPrice !== null
                      ? `₹${Number(r.currentPrice).toLocaleString(
                          "en-IN",
                          {
                            maximumFractionDigits: 2,
                          }
                        )}`
                      : "—"}
                  </td>

                  {/* ================================================= */}
                  {/* IPO SIZE */}
                  {/* ================================================= */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {r.totalIpoSizeCr !== null
                      ? Number(r.totalIpoSizeCr).toLocaleString(
                          "en-IN",
                          {
                            maximumFractionDigits: 0,
                          }
                        )
                      : "—"}
                  </td>

                  {/* ================================================= */}
                  {/* MARKET CAP */}
                  {/* ================================================= */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {r.marketCapCr !== null
                      ? Number(r.marketCapCr).toLocaleString(
                          "en-IN",
                          {
                            maximumFractionDigits: 0,
                          }
                        )
                      : "—"}
                  </td>

                  {/* ================================================= */}
                  {/* FIRST DAY GAIN */}
                  {/* ================================================= */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.firstDayGain} />
                  </td>

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.firstDayPurchaseGain} />
                  </td>

                  {/* ================================================= */}
                  {/* TOTAL GAIN */}
                  {/* ================================================= */}

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.totalGain} />
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderShareHoldingChange = () => {
    const arr = Array.isArray(technicalsData)
      ? technicalsData
      : Object.values(technicalsData || {});

    if (!arr.length) {
      return (
        <div className="text-sm text-red-500">
          No data available at the moment
        </div>
      );
    }

  // ============================================================
  // HELPERS
  // ============================================================

  const showIndustry = (industry) => {
    if (
      !industry ||
      ["N", "NEW"].includes(
        String(industry).trim().toUpperCase()
      )
    ) {
      return "—";
    }

    return String(industry)
      .split("/")
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x, i) => (
        <div
          key={i}
          className="
            block
            w-fit
            bg-gray-100
            text-gray-600
            text-xs
            px-2
            py-0.5
            rounded-full
            mb-1
            whitespace-nowrap
          "
        >
          {x}
        </div>
      ));
  };

  const formatQuarter = (dateStr) => {
    if (!dateStr) return "—";

    const d = new Date(`${dateStr}T00:00:00`);

    if (Number.isNaN(d.getTime())) {
      return dateStr;
    }

    const month = d
      .toLocaleString("en-US", {
        month: "short",
      })
      .toUpperCase();

    return `${month}-${d.getFullYear()}`;
  };

  const shareHoldingChange = (latest, previous) => {
    const latestNum = toNum(latest);
    const previousNum = toNum(previous);

    if (
      latestNum === null ||
      previousNum === null
    ) {
      return null;
    }

    // Shareholding change is percentage-point difference.
    // Example:
    // 5.54 - 5.29 = +0.25
    return latestNum - previousNum;
  };

  const shareholderCountChange = (latest, previous) => {
    const latestNum = toNum(latest);
    const previousNum = toNum(previous);

    if (
      latestNum === null ||
      previousNum === null ||
      previousNum === 0
    ) {
      return null;
    }

    return ((latestNum - previousNum) / previousNum) * 100;
  };

  const ChangeValue = ({
    value,
    digits = 2,
    suffix = "%",
  }) => {
    const n = toNum(value);

    if (n === null) {
      return (
        <span className="text-gray-500">
          —
        </span>
      );
    }

    const color =
      n > 0
        ? "text-green-600"
        : n < 0
        ? "text-red-600"
        : "text-gray-600";

    return (
      <span className={color}>
        {n > 0 ? "+" : ""}
        {n.toLocaleString("en-IN", {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        })}
        {suffix}
      </span>
    );
  };

  // ============================================================
  // PREPARE ROWS
  // ============================================================

  const rows = arr
    .filter((x) => {
      const shareHolding =
        x?.last2_shareHolding;

      return (
        shareHolding &&
        typeof shareHolding === "object" &&
        Object.keys(shareHolding).length > 0
      );
    })
    .map((x) => {
      const shareHolding =
        x.last2_shareHolding || {};

      // ISO YYYY-MM-DD keys sort correctly alphabetically.
      const quarterDates = Object.keys(
        shareHolding
      ).sort(
        (a, b) =>
          new Date(a).getTime() -
          new Date(b).getTime()
      );

      const latestDate =
        quarterDates.length > 0
          ? quarterDates[
              quarterDates.length - 1
            ]
          : null;

      const previousDate =
        quarterDates.length > 1
          ? quarterDates[
              quarterDates.length - 2
            ]
          : null;

      const latest =
        latestDate
          ? shareHolding[latestDate] || {}
          : {};

      const previous =
        previousDate
          ? shareHolding[previousDate] || {}
          : {};

      const currentPrice =
        x.currentPrice;

      const previousClose =
        x.previousClose;

      return {
        symbol: x.symbol,
        name: x.name,
        industry: x.industry,

        currentPrice,

        chg: pct(
          currentPrice,
          previousClose
        ),

        marketCapCr:
          toNum(x.marketCap) !== null
            ? toNum(x.marketCap) /
              10000000
            : null,

        latestQuarter:
          formatQuarter(latestDate),

        latestQuarterDate:
          latestDate
            ? new Date(
                latestDate
              ).getTime()
            : null,

        // --------------------------------
        // Promoters
        // --------------------------------

        promoters:
          toNum(
            latest.Promoters_Total
          ),

        promotersChange:
          shareHoldingChange(
            latest.Promoters_Total,
            previous.Promoters_Total
          ),

        // --------------------------------
        // FII
        // --------------------------------

        fii:
          toNum(
            latest.FIIs_Total
          ),

        fiiChange:
          shareHoldingChange(
            latest.FIIs_Total,
            previous.FIIs_Total
          ),

        // --------------------------------
        // DII
        // --------------------------------

        dii:
          toNum(
            latest.DIIs_Total
          ),

        diiChange:
          shareHoldingChange(
            latest.DIIs_Total,
            previous.DIIs_Total
          ),

        // --------------------------------
        // Government
        // --------------------------------

        government:
          toNum(
            latest.Government_Total
          ),

        governmentChange:
          shareHoldingChange(
            latest.Government_Total,
            previous.Government_Total
          ),

        // --------------------------------
        // Public
        // --------------------------------

        publicHolding:
          toNum(
            latest.Public_Total
          ),

        publicChange:
          shareHoldingChange(
            latest.Public_Total,
            previous.Public_Total
          ),

        // --------------------------------
        // Others
        // --------------------------------

        others:
          toNum(
            latest.Others_Total
          ),

        othersChange:
          shareHoldingChange(
            latest.Others_Total,
            previous.Others_Total
          ),

        // --------------------------------
        // Total shareholders
        // --------------------------------

        shareholders:
          toNum(
            latest.Shareholders_Total
          ),

        shareholdersChange:
          shareholderCountChange(
            latest.Shareholders_Total,
            previous.Shareholders_Total
          ),

        lastUpdateDate:
          x.lastUpdateDate,
      };
    });

  // ============================================================
  // SORTING
  // ============================================================

  const sortedRows = [...rows].sort(
    (a, b) => {
      const {
        key,
        direction,
      } = sortConfig;

      const dir =
        direction === "asc"
          ? 1
          : -1;

      // Industry alphabetical
      if (key === "industry") {
        return (
          String(
            a.industry || ""
          ).localeCompare(
            String(
              b.industry || ""
            )
          ) * dir
        );
      }

      // Name alphabetical
      if (key === "name") {
        return (
          String(
            a.name || a.symbol || ""
          ).localeCompare(
            String(
              b.name || b.symbol || ""
            )
          ) * dir
        );
      }

      const av = a?.[key];
      const bv = b?.[key];

      if (
        av === null ||
        av === undefined
      ) {
        if (
          bv === null ||
          bv === undefined
        ) {
          return 0;
        }

        return 1;
      }

      if (
        bv === null ||
        bv === undefined
      ) {
        return -1;
      }

      return (
        (Number(av) - Number(bv)) *
        dir
      );
    }
  );

  if (!sortedRows.length) {
    return (
      <div className="text-sm text-red-500">
        No shareholding data available
      </div>
    );
  }

  const lastUpdateDate =
    arr[0]?.lastUpdateDate ?? null;

  // ============================================================
  // TABLE
  // ============================================================

  return (
    <div>
      <div className="mt-2 text-gray-500">
        Updates daily 11 PM. Last updated -{" "}
        {formatDate(lastUpdateDate)}
      </div>

      <div className="text-xs sm:text-sm text-gray-400 mb-2">
        Tracked Symbols -{" "}
        {sortedRows.length}
      </div>

      <div className="overflow-x-auto">
        <table
          className="
            table-fixed
            border-collapse
            text-xs
            sm:text-sm
            text-gray-800
            min-w-[2300px]
            w-full
          "
        >
          <colgroup>
            {/* Name */}
            <col className="w-44 sm:w-72" />

            {/* Price */}
            <col className="w-24" />

            {/* Price Chg */}
            <col className="w-24" />

            {/* Market Cap */}
            <col className="w-28" />

            {/* Industry */}
            <col className="w-40" />

            {/* Quarter */}
            <col className="w-28" />

            {/* Promoter */}
            <col className="w-24" />
            <col className="w-24" />

            {/* FII */}
            <col className="w-24" />
            <col className="w-24" />

            {/* DII */}
            <col className="w-24" />
            <col className="w-24" />

            {/* Government */}
            <col className="w-24" />
            <col className="w-24" />

            {/* Public */}
            <col className="w-24" />
            <col className="w-24" />

            {/* Others */}
            <col className="w-24" />
            <col className="w-24" />

            {/* Shareholders */}
            <col className="w-32" />
            <col className="w-32" />
          </colgroup>

          <thead className="bg-gray-50 text-gray-600">
            <tr>
              {/* NAME */}
              <th
                onClick={() =>
                  setSortConfig(
                    (prev) => ({
                      key: "name",
                      direction:
                        prev.key ===
                          "name" &&
                        prev.direction ===
                          "desc"
                          ? "asc"
                          : "desc",
                    })
                  )
                }
                className="
                  sticky
                  left-0
                  z-30
                  bg-gray-50
                  border-r
                  px-2
                  sm:px-3
                  py-2
                  text-left
                  cursor-pointer
                  select-none
                "
              >
                <span
                  className={`inline-flex items-center gap-1 ${
                    sortConfig.key ===
                    "name"
                      ? "text-blue-600"
                      : ""
                  }`}
                >
                  Name

                  {sortConfig.key ===
                    "name" &&
                    (sortConfig.direction ===
                    "asc"
                      ? "↑"
                      : "↓")}
                </span>
              </th>

              {/* PRICE */}
              <th className="px-2 py-2 text-right">
                Price
              </th>

              <SortableTh
                label="Price"
                sortKey="chg"
                subLabel="Chg"
              />

              <SortableTh
                label="Market Cap"
                sortKey="marketCapCr"
                subLabel="Cr"
              />

              {/* INDUSTRY */}
              <th
                onClick={() =>
                  setSortConfig(
                    (prev) => ({
                      key: "industry",
                      direction:
                        prev.key ===
                          "industry" &&
                        prev.direction ===
                          "desc"
                          ? "asc"
                          : "desc",
                    })
                  )
                }
                className="
                  cursor-pointer
                  select-none
                  text-left
                  px-2
                  py-2
                "
              >
                <span
                  className={`inline-flex items-center gap-1 ${
                    sortConfig.key ===
                    "industry"
                      ? "text-blue-600"
                      : ""
                  }`}
                >
                  Industry

                  {sortConfig.key ===
                    "industry" &&
                    (sortConfig.direction ===
                    "asc"
                      ? "↑"
                      : "↓")}
                </span>
              </th>

              <SortableTh
                label="Latest Quarter"
                sortKey="latestQuarterDate"
              />

              <SortableTh
                label="Promoter"
                sortKey="promoters"
                subLabel="%"
              />

              <SortableTh
                label="Promoter"
                sortKey="promotersChange"
                subLabel="Chg"
              />

              <SortableTh
                label="FII"
                sortKey="fii"
                subLabel="%"
              />

              <SortableTh
                label="FII"
                sortKey="fiiChange"
                subLabel="Chg"
              />

              <SortableTh
                label="DII"
                sortKey="dii"
                subLabel="%"
              />

              <SortableTh
                label="DII"
                sortKey="diiChange"
                subLabel="Chg"
              />

              <SortableTh
                label="Government"
                sortKey="government"
                subLabel="%"
              />

              <SortableTh
                label="Government"
                sortKey="governmentChange"
                subLabel="Chg"
              />

              <SortableTh
                label="Public"
                sortKey="publicHolding"
                subLabel="%"
              />

              <SortableTh
                label="Public"
                sortKey="publicChange"
                subLabel="Chg"
              />

              <SortableTh
                label="Others"
                sortKey="others"
                subLabel="%"
              />

              <SortableTh
                label="Others"
                sortKey="othersChange"
                subLabel="Chg"
              />

              <SortableTh
                label="Shareholders"
                sortKey="shareholders"
                subLabel="Total"
              />

              <SortableTh
                label="Shareholders"
                sortKey="shareholdersChange"
                subLabel="Chg"
              />
            </tr>
          </thead>

          <tbody>
            {sortedRows.map(
              (r, idx) => (
                <tr
                  key={
                    r.symbol ||
                    idx
                  }
                  className={`group ${
                    idx % 2 === 0
                      ? "bg-white"
                      : "bg-gray-50"
                  } hover:bg-blue-50`}
                >
                  {/* NAME */}
                  <td
                    className={`
                      sticky
                      left-0
                      z-20
                      border-r
                      px-2
                      sm:px-3
                      py-2
                      font-medium
                      ${
                        idx % 2 ===
                        0
                          ? "bg-white"
                          : "bg-gray-50"
                      }
                      group-hover:bg-blue-50
                    `}
                  >
                    <a
                      href={`/symbol/${r.symbol}`}
                      target="_blank"
                      rel="noreferrer"
                      title={
                        r.name ||
                        r.symbol
                      }
                      className="
                        block
                        text-blue-600
                        hover:underline
                      "
                    >
                      <span className="block sm:hidden truncate max-w-[10.5rem]">
                        {r.name ||
                          r.symbol}
                      </span>

                      <span className="hidden sm:block">
                        {r.name ||
                          r.symbol}
                      </span>

                      <span className="block sm:hidden text-[10px] text-gray-500">
                        {r.symbol}
                      </span>
                    </a>
                  </td>

                  {/* PRICE */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {fmt(
                      r.currentPrice,
                      2
                    )}
                  </td>

                  {/* PRICE CHANGE */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ColorChange
                      value={r.chg}
                    />
                  </td>

                  {/* MARKET CAP */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {toNum(
                      r.marketCapCr
                    ) !== null
                      ? Math.round(
                          r.marketCapCr
                        ).toLocaleString(
                          "en-IN"
                        )
                      : "—"}
                  </td>

                  {/* INDUSTRY */}
                  <td className="px-2 py-2">
                    {showIndustry(
                      r.industry
                    )}
                  </td>

                  {/* QUARTER */}
                  <td className="px-2 py-2 text-right whitespace-nowrap font-medium">
                    {r.latestQuarter}
                  </td>

                  {/* PROMOTER */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {fmt(
                      r.promoters,
                      2
                    )}
                    %
                  </td>

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ChangeValue
                      value={
                        r.promotersChange
                      }
                    />
                  </td>

                  {/* FII */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {fmt(
                      r.fii,
                      2
                    )}
                    %
                  </td>

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ChangeValue
                      value={
                        r.fiiChange
                      }
                    />
                  </td>

                  {/* DII */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {fmt(
                      r.dii,
                      2
                    )}
                    %
                  </td>

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ChangeValue
                      value={
                        r.diiChange
                      }
                    />
                  </td>

                  {/* GOVERNMENT */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {fmt(
                      r.government,
                      2
                    )}
                    %
                  </td>

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ChangeValue
                      value={
                        r.governmentChange
                      }
                    />
                  </td>

                  {/* PUBLIC */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {fmt(
                      r.publicHolding,
                      2
                    )}
                    %
                  </td>

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ChangeValue
                      value={
                        r.publicChange
                      }
                    />
                  </td>

                  {/* OTHERS */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {fmt(
                      r.others,
                      2
                    )}
                    %
                  </td>

                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ChangeValue
                      value={
                        r.othersChange
                      }
                    />
                  </td>

                  {/* TOTAL SHAREHOLDERS */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {toNum(
                      r.shareholders
                    ) !== null
                      ? Math.round(
                          r.shareholders
                        ).toLocaleString(
                          "en-IN"
                        )
                      : "—"}
                  </td>

                  {/* SHAREHOLDER CHANGE */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <ChangeValue
                      value={
                        r.shareholdersChange
                      }
                      digits={2}
                      suffix="%"
                    />
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
  };

  const renderCrossScanner = (type = "golden") => {
    const isGolden = type === "golden";

    const dateKey = isGolden
      ? "golden_cross_date"
      : "death_cross_date";

    const priceKey = isGolden
      ? "golden_cross_price"
      : "death_cross_price";

    const arr = Array.isArray(technicalsData)
      ? technicalsData
      : Object.values(technicalsData || {});

    // --------------------------------------------------
    // Helpers
    // --------------------------------------------------

    const parseCrossDate = (value) => {
      if (!value) return null;

      const str = String(value).trim();

      let day, month, year;

      // Format 1: DD-MM-YYYY
      let match = str.match(/^(\d{2})-(\d{2})-(\d{4})$/);

      if (match) {
        day = Number(match[1]);
        month = Number(match[2]);
        year = Number(match[3]);
      } else {
        // Format 2: YYYY-MM-DD
        match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);

        if (!match) return null;

        year = Number(match[1]);
        month = Number(match[2]);
        day = Number(match[3]);
      }

      const date = new Date(year, month - 1, day);

      // Validate date
      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        return null;
      }

      return date;
    };

    const formatCrossDate = (value) => {
      const date = parseCrossDate(value);

      return date
        ? date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "—";
    };

    const daysSince = (value) => {
      const date = parseCrossDate(value);

      if (!date) return null;

      const today = new Date();

      const todayUTC = Date.UTC(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );

      const crossUTC = Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );

      return Math.floor(
        (todayUTC - crossUTC) / 86400000
      );
    };

    const formatMarketCap = (value) => {
      const num = toNum(value);

      if (num === null || num <= 0) return "—";

      return (num / 10000000).toLocaleString("en-IN", {
        maximumFractionDigits: 1,
      });
    };

    const showIndustry = (industry) => {
      if (
        !industry ||
        ["N", "NEW"].includes(
          String(industry).trim().toUpperCase()
        )
      ) {
        return "—";
      }

      return String(industry)
        .split("/")
        .map((x, i) => (
          <div
            key={i}
            className="block w-fit bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full mb-1"
          >
            {x.trim()}
          </div>
        ));
    };

    // --------------------------------------------------
    // Prepare rows
    // --------------------------------------------------

    const rows = arr
      .filter((x) => {
        const crossDate = parseCrossDate(x[dateKey]);
        const crossPrice = toNum(x[priceKey]);

        return (
          crossDate !== null &&
          crossPrice !== null &&
          crossPrice > 0
        );
      })
      .map((x) => {
        const currentPrice = toNum(x.currentPrice);
        const crossPrice = toNum(x[priceKey]);

        const crossDate = x[dateKey];

        return {
          symbol: x.symbol,
          name: x.name,
          industry: x.industry,

          // Price
          price: currentPrice,

          // 1D price change
          priceChg: pct(
            x.currentPrice,
            x.previousClose
          ),

          // Market Cap (raw rupees)
          marketCap: toNum(x.marketCap),

          // Cross information
          crossDate,

          crossDateTs:
            parseCrossDate(crossDate)?.getTime() ?? null,

          crossPrice,

          daysSince: daysSince(crossDate),

          // Percentage change since crossover
          changeSinceCross:
            currentPrice !== null && crossPrice > 0
              ? ((currentPrice - crossPrice) / crossPrice) * 100
              : null,

          // Last 10 trading days with >2% moves
          last10days_morethan_2pc:
            toNum(x.last10days_morethan_2pc),
        };
      });

    // --------------------------------------------------
    // Sorting
    // --------------------------------------------------

    const sortedRows = [...rows].sort((a, b) => {
      const { key, direction } = crossSort;

      const av = toNum(a[key]);
      const bv = toNum(b[key]);

      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;

      return direction === "asc"
        ? av - bv
        : bv - av;
    });

    const toggleCrossSort = (key) => {
      setCrossSort((prev) => ({
        key,
        direction:
          prev.key === key
            ? prev.direction === "desc"
              ? "asc"
              : "desc"
            : "desc",
      }));
    };

    const CrossSortableTh = ({
      label,
      sortKey,
      subLabel,
      align = "right",
    }) => {
      const active = crossSort.key === sortKey;

      return (
        <th
          onClick={() => toggleCrossSort(sortKey)}
          className={`px-3 py-2 cursor-pointer select-none whitespace-nowrap ${
            align === "left" ? "text-left" : "text-right"
          }`}
        >
          <div
            className={`flex flex-col ${
              align === "left"
                ? "items-start"
                : "items-end"
            }`}
          >
            <span className="flex items-center gap-1 text-blue-600">
              {label}

              {active &&
                (crossSort.direction === "asc" ? "↑" : "↓")}
            </span>

            {subLabel && (
              <span className="text-xs text-gray-400">
                {subLabel}
              </span>
            )}
          </div>
        </th>
      );
    };

    // --------------------------------------------------
    // Render
    // --------------------------------------------------

    return (
      <div className="w-full">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">

          <div className="flex items-center gap-2">

            <span
              className={`px-2 py-1 rounded text-xs font-semibold ${
                isGolden
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              {isGolden ? "Golden Cross" : "Death Cross"}
            </span>

            <span className="text-xs text-gray-500">
              50 DMA / 200 DMA crossover
            </span>

          </div>

          <span className="text-xs text-gray-500">
            Tracked Symbols: {sortedRows.length}
          </span>

        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">

          <table className="table-fixed border-collapse text-xs sm:text-sm text-gray-800 min-w-[1150px] w-full">

            <colgroup>
              <col className="w-52" />
              <col className="w-24" />
              <col className="w-28" />
              <col className="w-32" />
              <col className="w-44" />
              <col className="w-32" />
              <col className="w-28" />
              <col className="w-36" />
              <col className="w-28" />
            </colgroup>

            <thead className="bg-gray-100 text-gray-700">

              <tr>

                <th className="sticky left-0 z-30 bg-gray-100 px-3 py-2 text-left border-r border-gray-200">
                  Name
                </th>

                <th className="px-3 py-2 text-right">
                  Price
                </th>

                <CrossSortableTh
                  label="Price Chg"
                  sortKey="priceChg"
                  subLabel="(1D)"
                />

                <CrossSortableTh
                  label="M Cap"
                  sortKey="marketCap"
                  subLabel="(Cr.)"
                />

                <th className="px-3 py-2 text-left">
                  Industry
                </th>

                <CrossSortableTh
                  label={isGolden ? "Golden Cross" : "Death Cross"}
                  sortKey="crossDateTs"
                  subLabel="Date"
                />

                <CrossSortableTh
                  label="Days Since"
                  sortKey="daysSince"
                />

                <CrossSortableTh
                  label="Since Cross"
                  sortKey="changeSinceCross"
                  subLabel="(%)"
                />

                <CrossSortableTh
                  label="Last 10D"
                  sortKey="last10days_morethan_2pc"
                  subLabel="> 2%"
                />

              </tr>

            </thead>

            <tbody>

              {sortedRows.length === 0 ? (

                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No {isGolden ? "golden" : "death"} cross
                    records available.
                  </td>
                </tr>

              ) : (

                sortedRows.map((r, idx) => (

                  <tr
                    key={r.symbol || idx}
                    className={`border-t border-gray-100 hover:bg-blue-50 ${
                      idx % 2 === 0
                        ? "bg-white"
                        : "bg-gray-50"
                    }`}
                  >

                    {/* Name */}
                    <td className="sticky left-0 z-20 bg-inherit border-r border-gray-200 px-3 py-2">

                      <a
                        href={`/symbol/${r.symbol}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 hover:underline font-medium"
                      >
                        {r.name || r.symbol}
                      </a>

                    </td>

                    {/* Price */}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {fmt(r.price, 2)}
                    </td>

                    {/* Price Change */}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <ColorChange value={r.priceChg} />
                    </td>

                    {/* Market Cap */}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {formatMarketCap(r.marketCap)}
                    </td>

                    {/* Industry */}
                    <td className="px-3 py-2 text-left">

                      {showIndustry(r.industry)}

                    </td>

                    {/* Cross Date */}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {formatCrossDate(r.crossDate)}
                    </td>

                    {/* Days Since */}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {r.daysSince !== null
                        ? r.daysSince
                        : "—"}
                    </td>

                    {/* Change Since Cross */}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <ColorChange value={r.changeSinceCross} />
                    </td>

                    {/* Last 10D > 2% */}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {r.last10days_morethan_2pc !== null
                        ? r.last10days_morethan_2pc
                        : "—"}
                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </table>

        </div>

      </div>
    );
  };

  if (!user) {
    return (
      <div className="p-4 text-sm text-gray-600">
        Please log in to view this content.
      </div>
    );
  }

  return (
    <div className="p-4">

      {/* Must Read */}
      <div className="mb-4 border border-yellow-300 bg-yellow-50 rounded p-3 text-xs text-gray-700">
        <div className="font-semibold text-gray-800 mb-1">
          ⚠️ Must Read
        </div>
        <p>
          These scanners are automatically generated using internal models and provided strictly for <b>educational and informational purposes only</b>.
          They do <b>not</b> constitute buy or sell recommendations or investment advice.
        </p>
      </div>

      <div className="mt-2 mb-2 flex items-center gap-1 text-gray-500 text-sm">
        <span>For deeper comparison, try:</span>
        <a
          href="/playground"
          className="text-blue-600 hover:underline font-medium"
        >
          Playground
        </a>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b mb-4">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setShowExplanation(false);
            }}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition ${
              activeTab === tab
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Expand / Collapse button */}
      <button
        onClick={() => setShowExplanation((v) => !v)}
        className="flex items-center gap-1 text-xs border border-gray-300 rounded-full px-3 py-1 text-gray-700 hover:bg-gray-100 transition"
      >
        <span className="font-semibold">
          {showExplanation ? "−" : "+"}
        </span>
        <span>
          {showExplanation ? "Hide Explanation" : "Show Explanation"}
        </span>
      </button>

      {/* Content */}
      {showExplanation && (
        <div className="bg-gray-50 border rounded p-4 text-sm text-gray-700 leading-relaxed">
          <h2 className="font-semibold text-gray-800 mb-2">
            {activeTab} — Explanation
          </h2>
          {tips[activeTab]}
        </div>
      )}

      <div className="mt-3">
        {activeTab === "Monthly Cycles" ? (
          renderMonthlyCycles()
        ) : activeTab === "Price + OI + PCR" ? (
          renderPriceOiPcr()
        ) : activeTab === "Price + Delivery + ATS" ? (
          renderPriceDeliveryATS()
        ) : activeTab === "IPO Tracking" ? (
          ipoTracking()          
        ) : activeTab === "Share Holding Change" ? (
          renderShareHoldingChange()
        ) : activeTab === "Deep Recovery" ? (
          renderDeepRecovery()
        ) : activeTab === "Price + 52W H-L + SMA" ? (
          renderPrice52WHL_SMA()
        ) : activeTab === "Golden Cross" ? (
          renderCrossScanner("golden")

        ) : activeTab === "Death Cross" ? (
          renderCrossScanner("death")
        )  : (
          <div className="text-sm text-red-500">No data available at the moment</div>
        )}
      </div>
    </div>
  );
}

export default Scanners;
